import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { getCanonicalLocation, regionFromCountry } from "@/lib/canonicalLocation";
import { CSP_VERSION } from "@/config/csp";
import type { PremiumPlansFunnelSnapshot } from "@/config/bioTemplatePremiumPlans";
/**
 * PostHog Analytics Integration
 * Comprehensive tracking for user interactions, conversions, and session recording
 */

import posthog from "posthog-js";
import { getCumpleAttributionPostHogProps } from "@/lib/birthdayCtaAttribution";
import type { CumpleCtaSource } from "@/lib/birthdayCtaAttribution";
import { getDataFastStripeMetadata } from "@/lib/datafastAnalytics";
import {
  getPageEngagementContext,
  updateMaxScrollPct,
} from "@/lib/posthogEngagementContext";
import {
  getServerTemplateConfig,
  getWorkerGeoAnalyticsProps,
  setClientGeoEnrichmentOverlay,
} from "@/lib/serverTemplateDetection";
import { detectTrafficSource } from "@/lib/detectTrafficSource";
import { getStoredCookieConsent } from "@/lib/cookieConsentStorage";
import { isPostHogDisabledPath } from "@/lib/posthogPathGuard";

// PostHog configuration check
export const isPostHogConfigured = () => {
  if (typeof window === "undefined") return false;
  if (isPostHogDisabledPath(window.location.pathname)) return false;
  return (
    window.posthog &&
    typeof window.posthog.capture === "function"
  );
};

/** App UI host for session replay links (never the /ingest reverse proxy). */
export const POSTHOG_UI_HOST = "https://us.posthog.com";
/** Delfu Ocampo project — must match POSTHOG_PROJECT_ID on server. */
export const POSTHOG_PROJECT_ID =
  (import.meta.env.VITE_POSTHOG_PROJECT_ID as string | undefined)?.trim() || "422754";

const SESSION_ID_TOKEN_RE = /^[A-Za-z0-9_-]{8,128}$/;
const UUID_RE =
  /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

function cleanReplaySessionToken(raw: string): string {
  const t = raw.trim().replace(/[<>|"'\s]/g, "");
  if (!t || t.length < 8 || t.length > 128) return "";
  if (!SESSION_ID_TOKEN_RE.test(t) && !UUID_RE.test(t)) return "";
  return t;
}

/**
 * Fix broken replay URLs from posthog-js when api_host is a reverse proxy (/ingest).
 * Example bad: /ingest/project/phc_…/replay/<id>?t=0
 * Good: https://us.posthog.com/project/422754/replay/<id>?t=0
 */
export function normalizePostHogReplayUrlPostHog(
  rawUrl?: string | null,
  sessionId?: string | null,
): string | null {
  const raw = (rawUrl || "").trim();
  const sidHint = (sessionId || "").trim();

  let sid = "";
  let lookBack = "";

  if (raw) {
    const fromPath = raw.match(/\/replay\/([^/?#\s]+)/i);
    if (fromPath?.[1]) {
      try {
        const token = cleanReplaySessionToken(decodeURIComponent(fromPath[1]));
        if (token) sid = token;
      } catch {
        const token = cleanReplaySessionToken(fromPath[1]);
        if (token) sid = token;
      }
    }
    const t = raw.match(/[?&]t=(\d+)/);
    if (t?.[1]) lookBack = t[1];
  }

  if (!sid && sidHint) {
    const token = cleanReplaySessionToken(sidHint);
    if (token) sid = token;
    else {
      const m = sidHint.match(UUID_RE);
      if (m) sid = m[1];
    }
  }
  if (!sid && raw) {
    const m = raw.match(UUID_RE);
    if (m) sid = m[1];
  }

  if (!sid) {
    if (
      /^https?:\/\/[^\s]+posthog\.com\//i.test(raw) &&
      !raw.includes("/ingest")
    ) {
      return raw
        .replace(/\/project\/phc_[A-Za-z0-9]+\//, `/project/${POSTHOG_PROJECT_ID}/`)
        .split(/\s/)[0] || null;
    }
    return null;
  }

  let url = `${POSTHOG_UI_HOST}/project/${POSTHOG_PROJECT_ID}/replay/${sid}`;
  if (lookBack) url += `?t=${lookBack}`;
  return url;
}

// ─── UTM helpers (shared) ─────────────────────────────────────────────────────

/**
 * PostHog JS automatically captures UTM params and sets them as person
 * properties under standard names:
 *   last-touch  → utm_source, utm_medium, utm_campaign, utm_term, utm_content
 *   first-touch → $initial_utm_source, $initial_utm_medium, ...
 * We only parse UTMs here to fire our custom utm_attribution event.
 * See: https://posthog.com/docs/product-analytics/person-properties#default-person-properties
 */
function parseUTMFromSearch(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const keys = [
    "utm_source", "utm_medium", "utm_campaign",
    "utm_term", "utm_content", "utm_id",
    "gclid", "fbclid", "ttclid", "msclkid", "ref",
  ];
  const result: Record<string, string> = {};
  for (const key of keys) {
    const val = params.get(key);
    if (val) result[key] = val;
  }
  return result;
}

// ─── Super Properties ─────────────────────────────────────────────────────────

/**
 * Register super properties that PostHog automatically attaches to EVERY event.
 * Call once after PostHog loads. Avoids copy-pasting user_region/test_country
 * across every individual capture() call.
 */
export function registerSuperPropertiesPostHog() {
  if (!isPostHogConfigured()) return;

  const colorScheme = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches
    ? "dark"
    : "light";
  const standalone =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.navigator as any).standalone === true ||
    window.matchMedia?.("(display-mode: standalone)")?.matches === true;

  // ── IPLocate enrichment (Worker-injected via __TEMPLATE_CONFIG__) ────
  // Adds vpn/hosting/asn/neighborhood/geoSource as super properties — they
  // attach to every PostHog event automatically (pageviews, captures, etc.).
  // Lets you build cohorts like "AR + not VPN + Palermo" without code change.
  const geoProps = getWorkerGeoAnalyticsProps();

  posthog.register({
    site: BRAND_SOURCE_KEY, // brand identifier across PostHog projects
    // Región desde la VERDAD ÚNICA (país del Worker); localStorage solo si el
    // usuario cambió región a mano (user_region_manual). La key legacy
    // user_region (auto-persistida por la detección de browser) ya no se lee.
    user_region:
      localStorage.getItem("user_region_manual") ||
      regionFromCountry(getCanonicalLocation().country) ||
      "LATAM",
    test_country: localStorage.getItem("test_country") || null,
    app_version: import.meta.env.VITE_APP_VERSION || null,
    /** CSP policy version active for this session. Bumped when the effective
     * Content-Security-Policy changes so you can correlate events/violations
     * with policy revisions (aligns with the /report/ CSP feature). */
    csp_version: CSP_VERSION,
    /** Session-level hints for device / shell (startup-friendly breakdowns). */
    color_scheme_preference: colorScheme,
    pwa_display_standalone: standalone,
    /** IPLocate enrichment (filtered to defined values only) */
    ...Object.fromEntries(
      Object.entries(geoProps).filter(([, v]) => v !== undefined && v !== null),
    ),
  });
}

// ─── Person Profile Initialization ───────────────────────────────────────────

/**
 * Call once on PostHog `loaded` callback.
 *
 * PostHog JS already sets automatically (do NOT duplicate):
 *   $browser, $browser_version, $os, $device_type, $browser_language
 *   $screen_height/width, $viewport_height/width
 *   $referrer, $referring_domain, $initial_referrer, $initial_current_url
 *   utm_* (last-touch), $initial_utm_* (first-touch)
 *   $geoip_country_code/name, $geoip_city_name, $geoip_time_zone, etc.
 *
 * We only set properties that PostHog does NOT capture automatically.
 * Uses $set (updated each visit) and $set_once (set only the first time).
 */
export function initPersonPropertiesPostHog() {
  if (!isPostHogConfigured()) return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const connection = (navigator as any).connection;

  // ── Session / visit count ──────────────────────────────────────────────────
  // Increment total session count stored in localStorage (persists across tabs).
  // Use sessionStorage flag to count once per browser session, not per page load.
  const SESSION_STARTED_KEY = "ph_session_started";
  const TOTAL_SESSIONS_KEY  = "ph_total_sessions";
  const FIRST_VISIT_KEY     = "ph_first_visit";

  const isNewSession = !sessionStorage.getItem(SESSION_STARTED_KEY);
  if (isNewSession) {
    sessionStorage.setItem(SESSION_STARTED_KEY, "1");
  }
  const totalSessions = isNewSession
    ? parseInt(localStorage.getItem(TOTAL_SESSIONS_KEY) || "0") + 1
    : parseInt(localStorage.getItem(TOTAL_SESSIONS_KEY) || "1");
  if (isNewSession) localStorage.setItem(TOTAL_SESSIONS_KEY, String(totalSessions));

  const firstVisitDate = localStorage.getItem(FIRST_VISIT_KEY) || new Date().toISOString();
  if (!localStorage.getItem(FIRST_VISIT_KEY)) {
    localStorage.setItem(FIRST_VISIT_KEY, firstVisitDate);
  }
  const daysSinceFirstVisit = Math.floor(
    (Date.now() - new Date(firstVisitDate).getTime()) / (1000 * 60 * 60 * 24)
  );

  // ── IPLocate enrichment fields (Worker-injected via __TEMPLATE_CONFIG__) ──
  // Updated on every session ($set): values that may change (VPN status,
  // user's neighborhood if they move). Set once ($set_once): the very first
  // ISP / ASN we ever saw for this person (useful for fraud detection).
  const geoEnrichment = getWorkerGeoAnalyticsProps();

  // ── Traffic source (last-touch updated each session) ─────────────────────────
  const ts = detectTrafficSource();
  const params = new URLSearchParams(window.location.search);
  const utmCampaign = params.get("utm_campaign")?.trim() ?? "";
  const utmContent  = params.get("utm_content")?.trim()  ?? "";

  // $set — updated on every session
  const setProps: Record<string, unknown> = {
    user_region: localStorage.getItem("user_region") || "LATAM",
    test_country: localStorage.getItem("test_country") || null,
    cookies_enabled: navigator.cookieEnabled,
    do_not_track: navigator.doNotTrack === "1",
    // Network Information API — not captured by PostHog
    connection_type: connection?.effectiveType || null,
    connection_downlink_mbps: connection?.downlink || null,
    // Visit engagement
    session_count: totalSessions,
    days_since_first_visit: daysSinceFirstVisit,
    is_returning_visitor: totalSessions > 1,
    // IPLocate flags (updated each visit — VPN status can change)
    is_vpn: geoEnrichment.worker_vpn_detected === "true",
    is_hosting: geoEnrichment.worker_is_hosting === "true",
    current_asn: geoEnrichment.worker_asn_organization || null,
    current_neighborhood: geoEnrichment.worker_neighborhood || null,
    current_city: geoEnrichment.worker_city || null,
    current_province: geoEnrichment.worker_province || null,
    geo_source: geoEnrichment.worker_geo_source || null,
    // Last-touch traffic source (updated each visit)
    last_traffic_channel: ts.channel,
    last_traffic_source: ts.source,
    last_traffic_medium: ts.medium,
    last_in_app_browser: ts.inAppBrowser,
    ...(utmCampaign && { last_utm_campaign: utmCampaign }),
    ...(utmContent  && { last_utm_content:  utmContent  }),
    ...(ts.raw.referrer_domain && { last_referrer: ts.raw.referrer_domain }),
    ...(ts.raw.click_id && { last_click_id_type: ts.raw.click_id }),
  };

  // $set_once — written only when the property doesn't yet exist on the profile
  const setOnceProps: Record<string, unknown> = {
    first_seen: firstVisitDate,
    first_landing_page: window.location.pathname,
    first_user_region: localStorage.getItem("user_region") || "LATAM",
    // First-touch geo enrichment (immutable on the profile after first visit)
    first_asn: geoEnrichment.worker_asn_organization || null,
    first_country: geoEnrichment.worker_country || null,
    first_city: geoEnrichment.worker_city || null,
    first_province: geoEnrichment.worker_province || null,
    first_neighborhood: geoEnrichment.worker_neighborhood || null,
    first_is_vpn: geoEnrichment.worker_vpn_detected === "true",
    first_is_hosting: geoEnrichment.worker_is_hosting === "true",
    // First-touch traffic source (immutable — where they came from the first time)
    first_traffic_channel: ts.channel,
    first_traffic_source: ts.source,
    first_traffic_medium: ts.medium,
    ...(utmCampaign && { first_utm_campaign: utmCampaign }),
    ...(ts.raw.referrer_domain && { first_referrer: ts.raw.referrer_domain }),
  };

  posthog.setPersonProperties(setProps, setOnceProps);
}

// ─── Async IPLocate refresh (PostHog super props) ─────────────────────────────

type GeolocateEnrichResponse = {
  country?: string | null;
  province?: string | null;
  city?: string | null;
  region?: string | null;
  timezone?: string | null;
  isp?: string | null;
  source?: string;
  worker_geo_source?: string;
  privacy?: {
    is_vpn?: boolean;
    is_proxy?: boolean;
    is_tor?: boolean;
    is_relay?: boolean;
    is_hosting?: boolean;
  } | null;
  vpn_detected?: boolean;
  is_hosting?: boolean;
  asn_details?: { name?: string; asn?: string | number } | null;
  from_cache?: boolean;
};

function mapGeolocateToWorkerGeoProps(
  geo: GeolocateEnrichResponse,
): Record<string, string | undefined> {
  const privacy = geo.privacy;
  const vpn =
    geo.vpn_detected === true ||
    !!(
      privacy &&
      (privacy.is_vpn || privacy.is_proxy || privacy.is_tor || privacy.is_relay)
    );
  const hosting = geo.is_hosting === true || !!privacy?.is_hosting;
  const workerSource =
    geo.worker_geo_source ||
    (geo.from_cache ? "cache" : geo.source === "iplocate_io" ? "iplocate" : geo.source) ||
    undefined;
  const country =
    geo.country && geo.country !== "UNKNOWN" ? geo.country : undefined;
  const province = geo.province?.trim() || geo.region?.trim() || undefined;
  const city = geo.city?.trim() || undefined;
  const asnName = geo.isp || geo.asn_details?.name || undefined;
  const parts = [country, province, city].filter(
    (x): x is string => typeof x === "string" && x.length > 0,
  );
  return {
    worker_country: country,
    worker_province: province,
    worker_city: city,
    worker_geo_source: workerSource,
    worker_vpn_detected: vpn ? "true" : undefined,
    worker_is_hosting: hosting ? "true" : undefined,
    worker_asn_organization: asnName,
    worker_timezone: geo.timezone || undefined,
    geo_summary: parts.length ? parts.join(" · ") : undefined,
  };
}

let geoRefreshPromise: Promise<boolean> | null = null;

/** Fire-and-forget IPLocate enrichment via Worker API (tracking only, never blocks UX). */
export function startPostHogGeoRefresh(): void {
  if (geoRefreshPromise || typeof window === "undefined") return;
  geoRefreshPromise = refreshPostHogGeoEnrichment();
}

/** Await enrichment up to maxMs so first $pageview can include VPN/geo flags when cache is warm. */
export async function waitForPostHogGeoEnrichment(maxMs = 400): Promise<void> {
  if (!geoRefreshPromise) return;
  await Promise.race([
    geoRefreshPromise,
    new Promise<void>((resolve) => setTimeout(resolve, maxMs)),
  ]);
}

/**
 * Flush PostHog queue before hard navigation (Stripe Checkout redirect etc).
 * Prevents loss of events like vip_checkout_started, dual_cta_* , cta_click_vip.
 *
 * Uses short timeout to avoid blocking UX. Does NOT create extra events — only
 * flushes pending captures.
 *
 * Anti-saturation: only call on actual navigation to external checkout (not on
 * every interaction).
 */
export async function flushPostHogBeforeNavigation(timeoutMs = 400): Promise<void> {
  if (typeof window === "undefined" || !isPostHogConfigured()) return;

  const ph = (window as any).posthog;
  if (!ph || typeof ph.flush !== "function") return;

  try {
    await Promise.race([
      // posthog-js flush returns a promise in modern versions
      Promise.resolve(ph.flush()),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  } catch {
    // best-effort; never block navigation
  }
}

export async function refreshPostHogGeoEnrichment(): Promise<boolean> {
  if (typeof window === "undefined" || !isPostHogConfigured()) return false;

  const initial = getWorkerGeoAnalyticsProps();
  if (
    initial.worker_geo_source &&
    initial.worker_geo_source !== "cf_only" &&
    initial.worker_vpn_detected !== undefined
  ) {
    return true;
  }

  try {
    const response = await fetch("/api/geolocate?enrich=1", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!response.ok) return false;

    const geo = (await response.json()) as GeolocateEnrichResponse;
    const geoProps = mapGeolocateToWorkerGeoProps(geo);
    const hasEnrichment = Boolean(
      geoProps.worker_geo_source &&
        geoProps.worker_geo_source !== "cf_only",
    );
    if (!hasEnrichment && !geoProps.worker_vpn_detected) return false;

    // VERDAD ÚNICA DE LOCATION: el Worker manda. El fallback /api/geolocate
    // solo completa campos que el Worker no resolvió (VPN/ASN/hosting) — no
    // pisa country/province/city. `initial` trae la verdad del Worker.
    const merged: Record<string, string | undefined> = { ...geoProps };
    for (const [k, v] of Object.entries(initial)) {
      if (v !== undefined && v !== null && v !== "") merged[k] = v;
    }

    setClientGeoEnrichmentOverlay(merged);

    const registerProps = Object.fromEntries(
      Object.entries(merged).filter(([, v]) => v !== undefined && v !== null),
    );
    posthog.register(registerProps);

    posthog.setPersonProperties({
      is_vpn: merged.worker_vpn_detected === "true",
      is_hosting: merged.worker_is_hosting === "true",
      current_asn: merged.worker_asn_organization || null,
      current_city: merged.worker_city || null,
      current_province: merged.worker_province || null,
      geo_source: merged.worker_geo_source || null,
    });

    posthog.capture("geo_enrichment_refreshed", {
      worker_geo_source: geoProps.worker_geo_source,
      worker_vpn_detected: geoProps.worker_vpn_detected === "true",
      worker_is_hosting: geoProps.worker_is_hosting === "true",
      from_cache: geo.from_cache === true,
      api_source: geo.source || null,
      path: window.location.pathname,
    });

    return true;
  } catch {
    return false;
  }
}

// ─── UTM Attribution ──────────────────────────────────────────────────────────

/**
 * Fire a custom utm_attribution event whenever UTM params are detected.
 * PostHog already sets utm_* person properties automatically — we don't
 * duplicate that here, we just fire the event as a funnel signal.
 */
export function captureUTMAttributionPostHog(
  search: string,
  pathname: string
) {
  if (!isPostHogConfigured()) return;

  const utmParams = parseUTMFromSearch(search);
  if (Object.keys(utmParams).length === 0) return;

  posthog.capture("utm_attribution", {
    ...utmParams,
    page_path: pathname,
    referrer: document.referrer || null,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

// ─── Web Vitals ───────────────────────────────────────────────────────────────

/**
 * Observe Core Web Vitals (LCP, FCP, CLS, TTFB, INP) via PerformanceObserver
 * and send them to PostHog. Call once after PostHog is loaded.
 *
 * Key fix: LCP and CLS are captured on `visibilitychange` (page hide) so we
 * always get the final definitive value, not an intermediate one.
 */
function performanceObserverSupportsEntryType(type: string): boolean {
  const supported = (
    PerformanceObserver as unknown as { supportedEntryTypes?: readonly string[] }
  ).supportedEntryTypes;
  if (!Array.isArray(supported)) return true;
  return supported.includes(type);
}

export function trackWebVitalsPostHog() {
  if (!isPostHogConfigured() || typeof PerformanceObserver === "undefined") return;

  const capture = (name: string, value: number, extras?: Record<string, unknown>) => {
    posthog.capture("web_vital", {
      metric_name: name,
      metric_value: Math.round(name === "CLS" ? value * 1000 : value),
      metric_unit: name === "CLS" ? "score_x1000" : "ms",
      page_path: window.location.pathname,
      ...extras,
    });
  };

  // LCP – capture the last (largest) candidate on page hide, not on every update
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let lastLcp: any = null;
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      lastLcp = entries[entries.length - 1];
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden" && lastLcp) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        capture("LCP", (lastLcp as any).startTime, {
          element_tag: (lastLcp as { element?: Element }).element?.tagName,
        });
        lcpObserver.disconnect();
      }
    }, { once: true });
  } catch { /* unsupported */ }

  // FCP – fires once, safe to capture immediately
  try {
    const fcpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === "first-contentful-paint") {
          capture("FCP", entry.startTime);
          fcpObserver.disconnect();
        }
      }
    });
    fcpObserver.observe({ type: "paint", buffered: true });
  } catch { /* unsupported */ }

  // CLS – accumulate shifts, capture final value on page hide
  // Firefox (y otros) pueden registrar "Ignoring incompatible entryTypes: layout-shift" si no hay soporte.
  try {
    if (!performanceObserverSupportsEntryType("layout-shift")) {
      /* skip — evita ruido en consola */
    } else {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (!(entry as any).hadRecentInput) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            clsValue += (entry as any).value;
          }
        }
      });
      clsObserver.observe({ type: "layout-shift", buffered: true });

      document.addEventListener(
        "visibilitychange",
        () => {
          if (document.visibilityState === "hidden") {
            if (clsValue > 0) capture("CLS", clsValue);
            clsObserver.disconnect();
          }
        },
        { once: true },
      );
    }
  } catch { /* unsupported */ }

  // TTFB + DOM timings – available immediately from navigation entry
  try {
    const navObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const nav = entry as PerformanceNavigationTiming;
        if (nav.responseStart > 0) {
          capture("TTFB", nav.responseStart - nav.requestStart);
          capture("DOM_Interactive", nav.domInteractive);
          capture("DOM_Complete", nav.domComplete);
          navObserver.disconnect();
        }
      }
    });
    navObserver.observe({ type: "navigation", buffered: true });
  } catch { /* unsupported */ }

  // INP – Interaction to Next Paint; track worst interaction per session
  try {
    let worstInp = 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let worstInpEntry: any = null;
    const inpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const e = entry as any;
        if (e.duration > worstInp) {
          worstInp = e.duration;
          worstInpEntry = e;
        }
      }
    });
    inpObserver.observe({ type: "event", buffered: true, durationThreshold: 40 } as PerformanceObserverInit);

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden" && worstInpEntry) {
        capture("INP", worstInpEntry.duration, {
          interaction_type: worstInpEntry.name,
        });
        inpObserver.disconnect();
      }
    }, { once: true });
  } catch { /* unsupported */ }
}

/**
 * Track button clicks with PostHog.
 * Note: $browser, $os, $screen_*, $viewport_*, $browser_language are
 * auto-added by PostHog SDK to every event — no need to repeat them here.
 * user_region and test_country are registered as super properties.
 */
export function trackButtonClickPostHog(
  buttonText: string,
  buttonId: string,
  buttonType: string,
  service?: string,
  countryCode?: string,
  templateType?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("button_click", {
    button_text: buttonText,
    button_id: buttonId,
    button_type: buttonType,
    service: service || "general",
    page_path: window.location.pathname,
    page_title: document.title,
    country_code: countryCode || localStorage.getItem("test_country") || null,
    template_type: templateType,
    is_biolink_user: templateType === "BioLinkTemplate",
    timestamp: new Date().toISOString(),
  });

  if (buttonType.includes("modal_open") || buttonType.includes("payment")) {
    posthog.capture("conversion_funnel_step", {
      funnel_step: "modal_open",
      service: service || "general",
      button_text: buttonText,
      country_code: countryCode || localStorage.getItem("test_country") || null,
      template_type: templateType,
    });
  }

  if (buttonType.includes("tally_form")) {
    posthog.capture("form_start", {
      form_type: "tally",
      form_id: buttonId.includes("tally") ? "contact_form" : "unknown",
      button_text: buttonText,
      page_path: window.location.pathname,
    });
  }
}

/**
 * Track payment modal interactions with PostHog
 */
export function trackPaymentModalPostHog(
  action: "open" | "close" | "method_select",
  service: string,
  method?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("payment_modal_interaction", {
    action: action,
    service: service,
    payment_method: method || "unknown",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });

  // Track conversion funnel
  if (action === "method_select") {
    posthog.capture("conversion_funnel_step", {
      event_category: "Conversion",
      event_label: `${service} - Payment Method Selected`,
      funnel_step: "payment_method_selected",
      service: service,
      payment_method: method,
      country_code: localStorage.getItem("test_country") || "LATAM",
    });
  }
}

/**
 * Track template/layout usage with PostHog.
 * browser_language, timezone, is_mobile are auto-captured by PostHog SDK.
 */
export function trackTemplateUsagePostHog(
  template: string,
  region: string,
  features: string[],
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("template_usage", {
    template,
    region,
    features,
    page_path: window.location.pathname,
    page_title: document.title,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track Best Friend Instagram (Close Friends) click (membership subscription)
 * For conversion and revenue attribution
 */
export function trackBestFriendInstagramPostHog() {
  if (!isPostHogConfigured()) return;

  posthog.capture("bestfriend_instagram_click", {
    revenue: 6999,
    currency: "ARS",
    service: "bestfriend_instagram",
    page_path: window.location.pathname,
    template: "BaseTemplate",
    checkout_url: "/bestfriend-instagram",
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/**
 * Track social media link clicks with PostHog
 */
export function trackSocialClickPostHog(platform: string, linkUrl: string) {
  if (!isPostHogConfigured()) return;

  posthog.capture("social_click", {
    platform: platform.toLowerCase(),
    link_url: linkUrl,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/**
 * Track photo interactions with PostHog
 */
export function trackPhotoInteractionPostHog(
  action: "click" | "view" | "lightbox_open" | "lightbox_close",
  photoId: string | number,
  photoTitle?: string,
  pagePath?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("photo_interaction", {
    action: action,
    photo_id: String(photoId),
    photo_title: photoTitle || `Photo ${photoId}`,
    page_path: pagePath || window.location.pathname,
    timestamp: new Date().toISOString(),
    template_type:
      localStorage.getItem("user_region") === "US"
        ? "BioLinkTemplate"
        : "BaseTemplate",
  });
}

/**
 * Track video/reel interactions with PostHog
 */
export function trackVideoInteractionPostHog(
  action: "click" | "play" | "pause" | "complete",
  videoId: string | number,
  videoTitle?: string,
  embedId?: string,
  pagePath?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_interaction", {
    action: action,
    video_id: String(videoId),
    video_title: videoTitle || `Video ${videoId}`,
    embed_id: embedId,
    page_path: pagePath || window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/**
 * Track internal link clicks with PostHog
 */
export function trackLinkClickPostHog(linkText: string, linkUrl: string) {
  if (!isPostHogConfigured()) return;

  posthog.capture("link_click", {
    link_text: linkText,
    link_url: linkUrl,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    is_internal:
      linkUrl.startsWith("/") || linkUrl.includes(window.location.hostname),
  });
}

/**
 * Track page views with enhanced data.
 * When on "/", pass template so session replays can be filtered by template.
 *
 * Note: PostHog JS auto-adds to every event: $current_url, $browser, $os,
 * $screen_*, $viewport_*, $referrer, $browser_language, $device_type, GeoIP.
 * We only include custom properties not captured by the SDK.
 */
export function trackPageViewPostHog(
  pagePath?: string,
  pageTitle?: string,
  template?: string | null,
) {
  if (!isPostHogConfigured()) return;

  const payload: Record<string, unknown> = {
    // Standard $pageview properties PostHog expects
    $current_url: window.location.href,
    // Custom properties — not auto-captured by PostHog JS
    path: pagePath || window.location.pathname,
    title: pageTitle || document.title,
    hash: window.location.hash,
    user_region: localStorage.getItem("user_region") || "LATAM",
    test_country: localStorage.getItem("test_country"),
    timestamp: new Date().toISOString(),
    // Browser timezone (more precise than GeoIP tz for local-time analysis)
    browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  if (template) payload.template = template;

  const geo = getWorkerGeoAnalyticsProps();
  for (const [key, value] of Object.entries(geo)) {
    if (value !== undefined && value !== null) payload[key] = value;
  }

  posthog.capture("$pageview", payload);
}

/** IDs para enlazar Person / Session / replay en PostHog desde Slack (best-effort). */
export type PostHogCorrelationIds = {
  distinct_id: string | null;
  session_id: string | null;
  session_replay_url: string | null;
};

export function getPostHogCorrelationIdsPostHog(options?: {
  sessionReplay?: { withTimestamp?: boolean; timestampLookBack?: number };
}): PostHogCorrelationIds {
  if (!isPostHogConfigured()) {
    return { distinct_id: null, session_id: null, session_replay_url: null };
  }
  try {
    const distinct_id = posthog.get_distinct_id?.() ?? null;
    const sidRaw = posthog.get_session_id?.() ?? "";
    const session_id = sidRaw.length > 0 ? sidRaw : null;
    const sr = options?.sessionReplay;
    const replayOpts =
      sr &&
      (sr.withTimestamp === true || sr.timestampLookBack != null)
        ? {
            ...(sr.withTimestamp === true ? { withTimestamp: true as const } : {}),
            ...(sr.timestampLookBack != null
              ? { timestampLookBack: sr.timestampLookBack }
              : {}),
          }
        : undefined;
    const rawReplay =
      typeof posthog.get_session_replay_url === "function"
        ? posthog.get_session_replay_url(replayOpts) || null
        : null;
    // Always normalize: reverse-proxy api_host yields unusable /ingest/… links for Slack.
    const session_replay_url = normalizePostHogReplayUrlPostHog(
      rawReplay,
      session_id,
    );
    return { distinct_id, session_id, session_replay_url };
  } catch {
    return { distinct_id: null, session_id: null, session_replay_url: null };
  }
}

/**
 * Props listas para `capture()` y para `/api/track-funnel-failure` → Slack:
 * `ph_session_id`, `ph_distinct_id`, `ph_session_replay_url`, `slack_session_context` (una línea).
 */
export function getPostHogSlackSessionPropertiesPostHog(options?: {
  /** Enlazar replay cerca del momento actual (útil en CTAs). */
  replayNearNow?: boolean;
}): Record<string, string> {
  const replayOpts =
    options?.replayNearNow === true
      ? { sessionReplay: { withTimestamp: true as const, timestampLookBack: 45 } }
      : undefined;
  const c = getPostHogCorrelationIdsPostHog(replayOpts);
  const out: Record<string, string> = {};
  if (c.session_id) out.ph_session_id = c.session_id;
  if (c.distinct_id) out.ph_distinct_id = c.distinct_id;
  if (c.session_replay_url) out.ph_session_replay_url = c.session_replay_url;

  const bits: string[] = [];
  if (c.session_id) bits.push(`Session ${c.session_id}`);
  if (c.distinct_id) bits.push(`Person ${c.distinct_id}`);
  if (c.session_replay_url) bits.push(`Replay: ${c.session_replay_url}`);
  if (bits.length) out.slack_session_context = bits.join(" · ");

  return out;
}

/**
 * Rich client context for custom events (viewport, first landing URL, motion prefs).
 * Keeps values bounded; use {@link getUrlQueryKeysPostHog} instead of full query strings.
 */
export function getClientContextPropertiesPostHog(): Record<string, unknown> {
  if (typeof window === "undefined") return {};

  let entryLandingUrl: string | null = null;
  try {
    entryLandingUrl = sessionStorage.getItem("entry_page_url");
  } catch {
    /* */
  }

  const reducedMotion =
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav = navigator as any;
  const deviceMemory =
    typeof nav.deviceMemory === "number" ? nav.deviceMemory : null;

  return {
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    screen_width: window.screen?.width ?? null,
    screen_height: window.screen?.height ?? null,
    device_pixel_ratio:
      typeof window.devicePixelRatio === "number"
        ? Math.round(window.devicePixelRatio * 100) / 100
        : null,
    entry_landing_url: entryLandingUrl ? entryLandingUrl.slice(0, 500) : null,
    browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    prefers_reduced_motion: reducedMotion,
    device_memory_gb: deviceMemory,
    document_visibility: document.visibilityState,
  };
}

/**
 * Explicit PostHog IDs on high-value events — warehouse joins when `$session_id` parsing differs.
 */
export function getPostHogIdentityPropertiesPostHog(): Record<string, string> {
  const c = getPostHogCorrelationIdsPostHog();
  const out: Record<string, string> = {};
  if (c.session_id) out.ph_client_session_id = c.session_id;
  if (c.distinct_id) out.ph_client_distinct_id = c.distinct_id;
  return out;
}

/** Query param *names* (sorted, comma-separated) — no values, no PII. */
export function getUrlQueryKeysPostHog(search: string): string | null {
  try {
    const keys = [...new URLSearchParams(search).keys()];
    if (keys.length === 0) return null;
    return [...new Set(keys)].sort().join(",");
  } catch {
    return null;
  }
}

/**
 * Flat properties for `/api/track-funnel-failure` → Slack: PostHog correlation + client context.
 * Values are only string | number | boolean (API sanitizer).
 */
export function getSlackFunnelDiagnosticsExtraPostHog(options?: {
  replayNearNow?: boolean;
}): Record<string, string | number | boolean> {
  const slack = getPostHogSlackSessionPropertiesPostHog(
    options?.replayNearNow ? { replayNearNow: true } : undefined,
  );
  const out: Record<string, string | number | boolean> = { ...slack };
  Object.assign(out, getPostHogIdentityPropertiesPostHog());

  if (typeof window === "undefined") return out;

  const ctx = getClientContextPropertiesPostHog();
  if (typeof ctx.viewport_width === "number") out.viewport_w = ctx.viewport_width;
  if (typeof ctx.viewport_height === "number") out.viewport_h = ctx.viewport_height;
  if (typeof ctx.device_pixel_ratio === "number") out.dpr = ctx.device_pixel_ratio;
  if (typeof ctx.entry_landing_url === "string" && ctx.entry_landing_url)
    out.entry_landing_url = ctx.entry_landing_url.slice(0, 500);
  if (typeof ctx.browser_timezone === "string") out.browser_tz = ctx.browser_timezone;
  if (ctx.prefers_reduced_motion === true) out.prefers_reduced_motion = true;
  if (typeof ctx.device_memory_gb === "number") out.device_memory_gb = ctx.device_memory_gb;
  if (typeof ctx.document_visibility === "string")
    out.document_visibility = ctx.document_visibility;

  const qk = getUrlQueryKeysPostHog(window.location.search);
  if (qk) out.url_query_keys = qk;

  // ── Device / OS / Browser ────────────────────────────────────────────────────
  const ua = navigator.userAgent;
  const vw = window.innerWidth;
  out.device_type = vw < 768 ? "mobile" : vw < 1024 ? "tablet" : "desktop";
  out.os = /iPad|iPhone|iPod/.test(ua) && !("MSStream" in window)
    ? "ios"
    : /Android/.test(ua) ? "android"
    : /Windows/.test(ua) ? "windows"
    : /Mac OS X|Macintosh/.test(ua) ? "macos"
    : /Linux/.test(ua) ? "linux"
    : "unknown";
  out.browser = /Edg\//.test(ua) ? "edge"
    : /SamsungBrowser/.test(ua) ? "samsung"
    : /Opera|OPR\//.test(ua) ? "opera"
    : /Chrome\//.test(ua) ? "chrome"
    : /Firefox\//.test(ua) ? "firefox"
    : /Safari\//.test(ua) ? "safari"
    : "other";
  out.language = navigator.language || "";

  // ── Returning visitor ────────────────────────────────────────────────────────
  try {
    const sessions = parseInt(localStorage.getItem("ph_total_sessions") || "1");
    out.session_count = sessions;
    out.is_returning_visitor = sessions > 1;
  } catch { /* */ }

  // ── UTM campaign + content + term + click ID ────────────────────────────────
  try {
    const params = new URLSearchParams(window.location.search);
    const utmSource   = params.get("utm_source")?.trim()   ?? "";
    const utmMedium   = params.get("utm_medium")?.trim()   ?? "";
    const campaign    = params.get("utm_campaign")?.trim() ?? "";
    const content     = params.get("utm_content")?.trim()  ?? "";
    const term        = params.get("utm_term")?.trim()     ?? "";
    const clickId  = ["fbclid","gclid","ttclid","msclkid","igshid","twclid","li_fat_id"].find(id => params.has(id));
    if (utmSource) out.utm_source   = utmSource;
    if (utmMedium) out.utm_medium   = utmMedium;
    if (campaign)  out.utm_campaign = campaign;
    if (content)   out.utm_content  = content;
    if (term)      out.utm_term     = term;
    if (clickId)   out.click_id     = clickId;
  } catch { /* */ }

  // ── Referrer completo (no solo el dominio) ──────────────────────────────────
  // El dominio dice "vino de instagram", pero la URL dice "vino de
  // instagram.com/historia/abc/posts/123". Eso último permite saber QUÉ
  // contenido específico generó el tráfico. Truncamos para no pasarnos del
  // máximo del backend (200).
  try {
    const ref = document.referrer || "";
    if (ref) {
      out.referrer_full = ref.length > 200 ? ref.slice(0, 197) + "…" : ref;
      try {
        const refUrl = new URL(ref);
        const host = refUrl.hostname.replace(/^www\./, "");
        out.referrer_host = host;
        const path = refUrl.pathname;
        if (path && path !== "/") {
          out.referrer_path = path.length > 80 ? path.slice(0, 77) + "…" : path;
        }
      } catch { /* malformed referrer */ }
    }
  } catch { /* */ }

  // ── Hostname actual (multi-brand: distingue www.delfuocampo vs menu.* vs preview) ─
  try {
    out.host = window.location.hostname;
  } catch { /* */ }

  // ── Network info (effectiveType / downlink / RTT / saveData) ────────────────
  // Útil para diagnosticar "video no carga" vs "user en 2G" → distinguir bug
  // técnico vs limitación de red del usuario.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const conn = (navigator as any).connection;
    if (conn) {
      if (typeof conn.effectiveType === "string") out.net_effective_type = conn.effectiveType;
      if (typeof conn.downlink === "number")      out.net_downlink_mbps  = conn.downlink;
      if (typeof conn.rtt === "number")           out.net_rtt_ms         = conn.rtt;
      if (conn.saveData === true)                 out.net_save_data      = true;
    }
  } catch { /* */ }

  // ── Hardware concurrency (núcleos CPU) — proxy para clase de device ─────────
  try {
    if (typeof navigator.hardwareConcurrency === "number") {
      out.cpu_cores = navigator.hardwareConcurrency;
    }
  } catch { /* */ }

  // ── Orientación de pantalla (mobile portrait vs landscape) ──────────────────
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const orient = (window.screen as any)?.orientation?.type;
    if (typeof orient === "string") out.screen_orientation = orient;
  } catch { /* */ }

  // ── Cookie/storage state — útil para detectar incognito / cookies bloqueadas ─
  try {
    out.has_posthog_cookie = document.cookie.split(";").some(c => c.trim().startsWith("ph_"));
    out.cookies_enabled = navigator.cookieEnabled === true;
  } catch { /* */ }
  try {
    // localStorage no funciona en incógnito Safari / cookies bloqueadas
    const k = "__ls_probe__";
    localStorage.setItem(k, "1");
    localStorage.removeItem(k);
    out.local_storage_ok = true;
  } catch {
    out.local_storage_ok = false;
  }

  // ── Do Not Track signal del navegador ───────────────────────────────────────
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dnt = navigator.doNotTrack ?? (window as any).doNotTrack ?? null;
    if (dnt === "1" || dnt === "yes") out.dnt = true;
  } catch { /* */ }

  // ── Worker + IPLocate geo (de dónde son) — Railway/CF, no Vercel ───────────
  // truth: window.__TEMPLATE_CONFIG__ (Worker) + optional /api/geolocate overlay.
  // Always attach so Slack / track-funnel-failure show country · province · city
  // · ASN · VPN without relying on dead x-vercel-ip-* headers.
  try {
    const geo = getWorkerGeoAnalyticsProps();
    for (const [k, v] of Object.entries(geo)) {
      if (v === undefined || v === null || v === "") continue;
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        out[k] = v;
      }
    }
    // Alias for older Slack parsers that expect worker_asn_org
    if (out.worker_asn_organization && !out.worker_asn_org) {
      out.worker_asn_org = out.worker_asn_organization;
    }
  } catch { /* */ }

  // ── First-touch attribution (persistente en localStorage) ───────────────────
  // El visitante puede llegar desde IG hoy y volver vía Google mañana. Saber el
  // first-touch ayuda a entender qué canal originó el lead, no solo qué canal
  // cerró. Lo guardamos la primera vez que lo vemos y nunca lo sobreescribimos.
  try {
    const FT_KEY = "ph_first_touch_v1";
    const existing = localStorage.getItem(FT_KEY);
    if (existing) {
      try {
        const ft = JSON.parse(existing) as { src?: string; med?: string; ref?: string; ts?: number };
        if (ft.src)              out.first_touch_source   = String(ft.src).slice(0, 64);
        if (ft.med)              out.first_touch_medium   = String(ft.med).slice(0, 64);
        if (ft.ref)              out.first_touch_referrer = String(ft.ref).slice(0, 200);
        if (typeof ft.ts === "number") {
          const daysAgo = Math.max(0, Math.round((Date.now() - ft.ts) / (24 * 3600 * 1000)));
          out.days_since_first_touch = daysAgo;
        }
      } catch { /* corrupt JSON — overwrite below */ }
    } else {
      // Primera visita: capturar y persistir
      const params = new URLSearchParams(window.location.search);
      const src = params.get("utm_source") || (document.referrer ? "referral" : "direct");
      const med = params.get("utm_medium") || (document.referrer ? "referral" : "direct");
      const refDomain = (() => {
        try {
          return document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, "") : "";
        } catch { return ""; }
      })();
      localStorage.setItem(FT_KEY, JSON.stringify({
        src, med, ref: refDomain, ts: Date.now(),
      }));
      out.first_touch_source   = src;
      out.first_touch_medium   = med;
      if (refDomain) out.first_touch_referrer = refDomain;
      out.days_since_first_touch = 0;
    }
  } catch { /* */ }

  return out;
}

/** Clave sessionStorage de la URL de entrada (misma que usan los templates Bio). */
const ENTRY_PAGE_URL_KEY = "entry_page_url";

/**
 * Guarda la URL de entrada de la sesión (una vez por pestaña). Llamar en el
 * bootstrap (main.tsx) para que TODA página pueda reportar el entry en el
 * checkout, no solo los templates Bio que ya lo seteaban por su cuenta.
 */
export function captureEntryPageUrlPostHog(): void {
  if (typeof window === "undefined") return;
  try {
    if (!sessionStorage.getItem(ENTRY_PAGE_URL_KEY)) {
      sessionStorage.setItem(ENTRY_PAGE_URL_KEY, window.location.href);
    }
  } catch { /* sessionStorage bloqueado (incógnito Safari) */ }
}

/**
 * Atribución de origen para metadata de checkout (Stripe/MP): desde qué página
 * salió el pago (`landing_path`), por dónde entró la sesión (`entry_page`),
 * referrer y UTM/click ID. El webhook la reenvía al Slack de `sale_confirmed` —
 * sin esto la venta llega "ciega": monto y email pero no QUÉ página la generó.
 * Valores cortos (≤200 chars) para respetar límites de metadata de Stripe/MP.
 */
export function getCheckoutAttributionMetadataPostHog(): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof window === "undefined") return out;
  if (BRAND_SOURCE_KEY) out.source = BRAND_SOURCE_KEY;
  try {
    out.landing_path = window.location.pathname.slice(0, 180) || "/";
  } catch { /* */ }
  try {
    // Template asignado por país (admin → Worker → __TEMPLATE_CONFIG__). Sin
    // esto "landing_path: /" no dice QUÉ renderizó la home (DualCTAVideoLanding
    // vs BaseTemplate vs …) — que es lo que el operador configura en el admin.
    const tpl = getServerTemplateConfig()?.templateName?.trim();
    if (tpl) out.landing_template = tpl.slice(0, 80);
  } catch { /* */ }
  let entryUrl = "";
  try {
    entryUrl = sessionStorage.getItem(ENTRY_PAGE_URL_KEY) ?? "";
    if (entryUrl) {
      try {
        const u = new URL(entryUrl);
        out.entry_page = (u.pathname + u.search).slice(0, 180);
      } catch {
        out.entry_page = entryUrl.slice(0, 180);
      }
    }
  } catch { /* */ }
  try {
    if (document.referrer) {
      const ref = new URL(document.referrer);
      out.referrer_host = ref.hostname.replace(/^www\./, "").slice(0, 100);
      if (ref.pathname && ref.pathname !== "/") {
        out.referrer_path = ref.pathname.slice(0, 100);
      }
    }
  } catch { /* referrer malformado */ }
  try {
    // UTM: URL actual primero; si la SPA navegó y los perdió, la URL de entrada.
    const paramSets = [new URLSearchParams(window.location.search)];
    if (entryUrl) {
      try { paramSets.push(new URLSearchParams(new URL(entryUrl).search)); } catch { /* */ }
    }
    for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
      const v = paramSets.map((p) => p.get(key)?.trim()).find(Boolean);
      if (v) out[key] = v.slice(0, 100);
    }
    const clickId = ["fbclid", "gclid", "ttclid", "msclkid", "igshid", "twclid", "li_fat_id"]
      .find((id) => paramSets.some((p) => p.has(id)));
    if (clickId) out.click_id = clickId;
  } catch { /* */ }
  return out;
}

/**
 * IDs PostHog cortos para metadata de Stripe (límite ~500 chars/valor; no incluir replay URL).
 * Permite que webhooks (`payment_completed`) enlacen el Slack con la sesión del cliente.
 * Incluye también la atribución de página/origen (landing, entry, referrer, UTM).
 */
export function getPostHogStripeMetadataPostHog(): Record<string, string> {
  const c = getPostHogCorrelationIdsPostHog();
  const out: Record<string, string> = getCheckoutAttributionMetadataPostHog();
  if (c.session_id) out.ph_session_id = c.session_id.slice(0, 128);
  if (c.distinct_id) out.ph_distinct_id = c.distinct_id.slice(0, 256);
  // DataFast revenue attribution IDs (cookies set by script.js on data-domain).
  // Spread into Checkout Session / PaymentIntent metadata for Method 1 & 2.
  Object.assign(out, getDataFastStripeMetadata());
  return out;
}

/** Clave sessionStorage — una ID estable por pestaña para correlacionar clicks BaseTemplate. */
export const POSTHOG_BASE_TEMPLATE_TAB_SESSION_KEY = "base_template_tab_session_id_v1";

export function getOrCreateTabSessionId(storageKey: string): string {
  if (typeof window === "undefined") return "";
  try {
    let id = sessionStorage.getItem(storageKey);
    if (!id?.trim()) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `ts_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(storageKey, id);
    }
    return id;
  } catch {
    return `ts_${Date.now()}`;
  }
}

const PREMIUM_INVITE_FUNNEL_REG_KEYS = [
  "pi_invite_session_id",
  "pi_funnel_currency",
  "pi_language_base",
  "pi_special_offer_active",
  "pi_has_vip_mux",
  "pi_vip_plan_card_enabled",
  "pi_snapshot_version",
  "pi_shop_region",
  "pi_stripe_intl_currency",
  "pi_telegram_basic_gateway",
  "ph_session_id",
  "ph_distinct_id",
  "ph_session_replay_url",
  "slack_session_context",
] as const;

export type PremiumInviteFunnelRegisterMetaPostHog = {
  shop_region: "AR" | "intl";
  /** Solo aplica cuando shop_region === intl (EUR vs USD Stripe). */
  stripe_intl_currency?: "USD" | "EUR";
  /** Pasarela prevista para el plan grupo/basic. */
  telegram_basic_intended_gateway: "mercadopago" | "stripe";
};

/**
 * Super-properties mientras el visitante está en BioTemplatePremiumSimple (cualquier ruta).
 * Todo `captureEvent` hereda invite session + snapshot + PostHog replay linkage.
 */
export function registerPremiumInviteFunnelContextPostHog(
  inviteSessionId: string,
  snapshot: PremiumPlansFunnelSnapshot,
  funnelMeta?: PremiumInviteFunnelRegisterMetaPostHog,
): void {
  if (!isPostHogConfigured()) return;
  const slack = getPostHogSlackSessionPropertiesPostHog();
  posthog.register({
    pi_invite_session_id: inviteSessionId,
    pi_funnel_currency: snapshot.currency,
    pi_language_base: snapshot.language_base,
    pi_special_offer_active: snapshot.special_offer_active,
    pi_has_vip_mux: snapshot.has_vip_mux,
    pi_vip_plan_card_enabled: snapshot.vip_plan_card_enabled,
    pi_snapshot_version: snapshot.snapshot_version,
    ...(funnelMeta
      ? {
          pi_shop_region: funnelMeta.shop_region,
          ...(funnelMeta.stripe_intl_currency
            ? { pi_stripe_intl_currency: funnelMeta.stripe_intl_currency }
            : {}),
          pi_telegram_basic_gateway: funnelMeta.telegram_basic_intended_gateway,
        }
      : {}),
    ...slack,
  });
}

export function unregisterPremiumInviteFunnelContextPostHog(): void {
  if (!isPostHogConfigured()) return;
  try {
    (posthog as unknown as { unregister?: (keys: string[]) => void }).unregister?.([
      ...PREMIUM_INVITE_FUNNEL_REG_KEYS,
    ]);
  } catch {
    /* */
  }
}

/**
 * Evento único por carga — usar en funnels “Session started” y para ver riesgos (ej. sin Mux).
 */
export function trackPremiumInviteFunnelSessionPostHog(
  inviteSessionId: string,
  snapshot: PremiumPlansFunnelSnapshot,
  context: {
    country_code: string;
    region: string;
    funnel_risk_flags?: string[];
    shop_region: "AR" | "intl";
    stripe_intl_currency?: "USD" | "EUR";
    telegram_basic_intended_gateway: "mercadopago" | "stripe";
  },
): void {
  if (!isPostHogConfigured()) return;
  const slack = getPostHogSlackSessionPropertiesPostHog();
  const geo = getWorkerGeoAnalyticsProps();
  const geoFlat = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;

  posthog.capture("premium_invite_funnel_session", {
    invite_session_id: inviteSessionId,
    ...snapshot,
    country_code: context.country_code,
    region: context.region,
    shop_region: context.shop_region,
    ...(context.stripe_intl_currency
      ? { stripe_intl_currency: context.stripe_intl_currency }
      : {}),
    telegram_basic_intended_gateway: context.telegram_basic_intended_gateway,
    page_path: window.location.pathname,
    page_url: window.location.href,
    funnel_risk_flags: context.funnel_risk_flags,
    user_region: localStorage.getItem("user_region") || "LATAM",
    timestamp: new Date().toISOString(),
    ...geoFlat,
    ...slack,
  });
}

const BASE_TEMPLATE_REG_KEYS = [
  "base_tab_session_id",
  "base_template_surface",
  "base_country_worker",
  "base_instagram_in_app",
] as const;

const BASE_TEMPLATE_GEO_AND_PH_KEYS = [
  "ph_session_id",
  "ph_distinct_id",
  "ph_session_replay_url",
  "slack_session_context",
  "worker_country",
  "worker_cf_country",
  "worker_province",
  "worker_city",
  "worker_timezone",
  "worker_detection_method",
  "geo_summary",
] as const;

export function registerBaseTemplateSessionContextPostHog(
  tabSessionId: string,
  opts: {
    templateSurface: string;
    countryCode?: string;
    isInstagramInApp: boolean;
  },
): void {
  if (!isPostHogConfigured()) return;
  const slack = getPostHogSlackSessionPropertiesPostHog();
  const geo = getWorkerGeoAnalyticsProps();
  const geoFlat = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;
  posthog.register({
    base_tab_session_id: tabSessionId,
    base_template_surface: opts.templateSurface,
    base_country_worker: opts.countryCode || undefined,
    base_instagram_in_app: opts.isInstagramInApp,
    ...slack,
    ...geoFlat,
  });
}

/**
 * Super-properties while on DualCTAVideoLanding — every capture inherits
 * template / component / variant / price / mux / traffic (admin hub).
 */
export function registerDualCtaExperienceContextPostHog(
  snapshot: Record<string, string | number | boolean | null | undefined>,
): void {
  if (!isPostHogConfigured()) return;
  const clean: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(snapshot)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
      clean[k] = v;
    }
  }
  const slack = getPostHogSlackSessionPropertiesPostHog();
  posthog.register({
    ...clean,
    ...slack,
  });
}

export function unregisterBaseTemplateSessionContextPostHog(): void {
  if (!isPostHogConfigured()) return;
  try {
    (posthog as unknown as { unregister?: (keys: string[]) => void }).unregister?.([
      ...BASE_TEMPLATE_REG_KEYS,
      ...BASE_TEMPLATE_GEO_AND_PH_KEYS,
    ]);
  } catch {
    /* */
  }
}

export function trackBaseTemplateFunnelSessionPostHog(args: {
  tabSessionId: string;
  templateSurface: string;
  countryCode?: string;
  isInstagramInApp: boolean;
  features: string[];
}): void {
  if (!isPostHogConfigured()) return;
  const slack = getPostHogSlackSessionPropertiesPostHog();
  const geo = getWorkerGeoAnalyticsProps();
  const geoFlat = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;

  posthog.capture("base_template_funnel_session", {
    base_tab_session_id: args.tabSessionId,
    template_surface: args.templateSurface,
    country_code: args.countryCode,
    is_instagram_in_app: args.isInstagramInApp,
    features: args.features,
    page_path: window.location.pathname,
    page_url: window.location.href,
    user_region: localStorage.getItem("user_region") || "LATAM",
    timestamp: new Date().toISOString(),
    ...geoFlat,
    ...slack,
  });
}

const BIO_PREMIUM_REGISTER_KEYS = [
  "bio_premium_flow_trace_id",
  "bio_premium_tab_session_id",
  "template_funnel",
] as const;

/**
 * Super-properties en todos los eventos mientras el visitante está en BioTemplatePremium
 * — correlaciona clics/checkout con Slack y Stripe `flow_trace_id`.
 */
export function registerBioPremiumFunnelContextPostHog(
  flowTraceId: string,
  tabSessionId: string,
): void {
  if (!isPostHogConfigured()) return;
  posthog.register({
    bio_premium_flow_trace_id: flowTraceId,
    bio_premium_tab_session_id: tabSessionId,
    template_funnel: "BioTemplatePremium",
  });
}

export function unregisterBioPremiumFunnelContextPostHog(): void {
  if (!isPostHogConfigured()) return;
  try {
    (posthog as unknown as { unregister?: (keys: string[]) => void }).unregister?.([
      ...BIO_PREMIUM_REGISTER_KEYS,
    ]);
  } catch {
    /* */
  }
}

/** Inicio explícito de sesión funnel (pricing + país) — usar en dashboards y Slack linkage. */
export function trackBioPremiumFunnelSessionStartedPostHog(args: {
  flowName: string;
  flowTraceId: string;
  tabSessionId: string;
  country: string;
  currency: "ARS" | "USD";
  language: string;
  provider: "mercadopago" | "stripe";
  snapshot: PremiumPlansFunnelSnapshot;
  standard_plan: { price: number; productName: string };
  vip_plan?: { price: number; productName: string; isDisabled?: boolean };
}): void {
  if (!isPostHogConfigured()) return;

  const slack = getPostHogSlackSessionPropertiesPostHog();

  posthog.capture("bio_premium_funnel_session_started", {
    template_type: "BioTemplatePremium",
    flow_name: args.flowName,
    flow_trace_id: args.flowTraceId,
    tab_session_id: args.tabSessionId,
    country_code: args.country,
    gateway_provider: args.provider,
    locale_full: args.language,
    standard_price_live: args.standard_plan.price,
    standard_product_name: args.standard_plan.productName,
    vip_price_live: args.vip_plan?.price ?? null,
    vip_product_name: args.vip_plan?.productName ?? null,
    vip_card_disabled: Boolean(args.vip_plan?.isDisabled),
    ...slack,
    page_path: window.location.pathname,
    page_url: window.location.href,
    user_region: localStorage.getItem("user_region") || "LATAM",
    timestamp: new Date().toISOString(),
    ...args.snapshot,
  });

  posthog.capture("conversion_funnel_step", {
    funnel_step: "bio_premium_session_started",
    template_type: "BioTemplatePremium",
    flow_name: args.flowName,
    flow_trace_id: args.flowTraceId,
    tab_session_id: args.tabSessionId,
    country_code: args.country,
    currency: args.currency,
    gateway_provider: args.provider,
  });
}

/** Tiempo total en landing premium — disparar en `pagehide`/unmount (evita pérdidas en SPA). */
export function trackPremiumFunnelTimeOnPagePostHog(params: {
  template_type: "BioTemplatePremium" | "BioTemplatePremiumSimple";
  time_on_page_seconds: number;
  flow_name?: string;
  flow_trace_id?: string;
  tab_session_id?: string;
  session_id?: string;
  country?: string;
}) {
  if (!isPostHogConfigured()) return;

  posthog.capture("premium_funnel_time_on_page", {
    ...params,
    page_path: typeof window !== "undefined" ? window.location.pathname : null,
    page_url: typeof window !== "undefined" ? window.location.href : null,
    user_region:
      typeof window !== "undefined"
        ? localStorage.getItem("user_region") || "LATAM"
        : null,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Resumen de engagement con el video VIP (max posición vista vs duración del asset).
 * `watch_seconds_max` suele provenir del máximo `currentTime` durante el modal (Mux telemetry).
 */
export function trackPremiumVipVideoWatchSummaryPostHog(params: {
  template_type: "BioTemplatePremium" | "BioTemplatePremiumSimple";
  flow_name?: string;
  flow_trace_id?: string;
  tab_session_id?: string;
  session_id?: string;
  country?: string;
  playback_id?: string;
  currency?: string;
  exit_reason: "modal_close" | "cta_click" | "ended";
  watch_seconds_max: number;
  video_duration_seconds: number | null;
}) {
  if (!isPostHogConfigured()) return;

  const dur = params.video_duration_seconds;
  const capped =
    dur != null && dur > 0
      ? Math.min(Math.max(0, params.watch_seconds_max), dur)
      : Math.max(0, params.watch_seconds_max);
  const pct =
    dur != null && dur > 0 ? Math.round((capped / dur) * 1000) / 10 : null;

  posthog.capture("premium_vip_video_watch_summary", {
    template_type: params.template_type,
    flow_name: params.flow_name,
    flow_trace_id: params.flow_trace_id,
    tab_session_id: params.tab_session_id,
    session_id: params.session_id,
    country_code: params.country,
    playback_id: params.playback_id,
    currency: params.currency,
    exit_reason: params.exit_reason,
    watch_seconds_max: Math.round(params.watch_seconds_max * 100) / 100,
    video_duration_seconds: dur,
    watch_seconds_capped: Math.round(capped * 100) / 100,
    percent_complete_estimate: pct,
    page_path: typeof window !== "undefined" ? window.location.pathname : null,
    user_region:
      typeof window !== "undefined"
        ? localStorage.getItem("user_region") || "LATAM"
        : null,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Vista del embudo premium simple (BioTemplatePremiumSimple).
 * Se dispara en **cualquier** ruta donde monte el template (p. ej. `/` por Worker o `/invitacion-premium`).
 */
export function trackPremiumInvitePageViewPostHog(params: {
  country: string;
  language: string;
  session_id?: string;
  email_prefilled?: boolean;
  recovery_source?: "recovery_email";
  /** Precios/planMux — correlación con Slack y funnel bio premium */
  plans_funnel_snapshot?: PremiumPlansFunnelSnapshot;
  /** Canal Telegram público gratuito (VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL) visible en la página */
  has_free_telegram_url?: boolean;
}) {
  if (!isPostHogConfigured() || typeof window === "undefined") return;

  const pagePath =
    window.location.pathname.replace(/\/$/, "") || "/";
  const dedicatedInviteRoute =
    pagePath === "/invitacion-premium" ||
    pagePath === "/invitacion" ||
    pagePath === "/premium-invite";
  const invite_surface = dedicatedInviteRoute
    ? "dedicated_route"
    : "worker_bio_home";
  const shopRegion =
    String(params.country ?? "")
      .trim()
      .toUpperCase() === "AR"
      ? "AR"
      : "intl";

  const slack = getPostHogSlackSessionPropertiesPostHog();

  posthog.capture("premium_invite_page_view", {
    page_path: pagePath,
    invite_surface,
    shop_region: shopRegion,
    $current_url: window.location.href,
    country: params.country,
    language: params.language,
    session_id: params.session_id || null,
    email_prefilled: params.email_prefilled ?? false,
    recovery_source: params.recovery_source ?? null,
    has_free_telegram_url: params.has_free_telegram_url ?? false,
    ...(params.plans_funnel_snapshot ?? {}),
    ...slack,
    ...parseUTMFromSearch(window.location.search),
    user_region: localStorage.getItem("user_region") || "LATAM",
    test_country: localStorage.getItem("test_country"),
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track invitacion-mejores-amigos (Best Friends Instagram) / grupo-privado (Telegram Groups) page events (view, CTA click, close).
 * Use page_path in props to distinguish routes; defaults to /invitacion-mejores-amigos.
 */
export function trackInvitacionGrupoPostHog(
  action: "view" | "cta_click" | "close",
  props?: {
    page_path?: string;
    cta_url?: string;
    protection_violation?: string;
    [key: string]: any; // Allow additional properties for protection tracking
  },
) {
  if (!isPostHogConfigured()) return;

  const pagePath =
    props?.page_path ??
    (props as { current_path?: string })?.current_path ??
    "/invitacion-mejores-amigos";

  posthog.capture("invitacion_mejores_amigos", {
    ...props,
    action,
    page_path: pagePath,
    page_url: window.location.href,
    referrer: document.referrer,
    user_region: localStorage.getItem("user_region") || "LATAM",
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track scroll depth
 */
export function trackScrollDepthPostHog(scrollPercentage: number) {
  if (!isPostHogConfigured()) return;

  updateMaxScrollPct(scrollPercentage);
  posthog.capture("scroll_depth", {
    scroll_percentage: scrollPercentage,
    ...getPageEngagementContext(),
  });
}

/**
 * Track time spent on page
 */
export function trackTimeOnPagePostHog(timeSpentSeconds: number) {
  if (!isPostHogConfigured()) return;

  posthog.capture("time_on_page", {
    time_spent_seconds: timeSpentSeconds,
    ...getPageEngagementContext({ time_spent_seconds: timeSpentSeconds }),
  });
}

/**
 * Track user identification.
 * browser, language, timezone, screen_resolution are auto-captured by PostHog.
 */
export function identifyUserPostHog(
  userId: string,
  properties?: Record<string, any>,
) {
  if (!isPostHogConfigured()) return;

  posthog.identify(userId, {
    ...properties,
    user_region: localStorage.getItem("user_region") || "LATAM",
    test_country: localStorage.getItem("test_country") || null,
  });
}

/**
 * Set user properties
 */
export function setUserPropertiesPostHog(properties: Record<string, any>) {
  if (!isPostHogConfigured()) return;

  posthog.setPersonProperties({
    ...properties,
    last_updated: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
    test_country: localStorage.getItem("test_country"),
  });
}

/**
 * Track conversion events
 */
export function trackConversionPostHog(
  conversionType: string,
  service: string,
  value?: number,
  currency?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("conversion", {
    conversion_type: conversionType,
    service: service,
    value: value,
    currency: currency || "USD",
    page_path: window.location.pathname,
    user_region: localStorage.getItem("user_region") || "LATAM",
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track errors and exceptions.
 * user_agent is auto-captured by PostHog SDK on every event.
 */
export function trackErrorPostHog(error: Error, context?: Record<string, any>) {
  if (!isPostHogConfigured()) return;

  posthog.capture("$exception", {
    $exception_message: error.message,
    $exception_type: error.name,
    $exception_stack_trace_raw: error.stack,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...context,
  });
}

/**
 * Start session recording (force=true when SDK supports it — overrides remote pause).
 */
export function startSessionRecordingPostHog() {
  if (!isPostHogConfigured()) return;

  try {
    // posthog-js ≥1.2xx: force restarts even after linked-flag / sample stop
    const start = posthog.startSessionRecording as
      | ((force?: boolean) => void)
      | undefined;
    start?.(true);
  } catch {
    /* ignore */
  }
}

/**
 * Stop session recording
 */
export function stopSessionRecordingPostHog() {
  if (!isPostHogConfigured()) return;

  try {
    posthog.stopSessionRecording();
  } catch (error) {}
}

/**
 * Restore PostHog opt-in/out from the cookie banner decision.
 * GA consent is updated in useGoogleConsent; PostHog must be synced separately.
 *
 * Undecided (null): leave capture enabled so session recording can start on
 * public pages (brand policy = full coverage). Declined: hard opt-out.
 * Accepted: force opt-in + start recording.
 */
export function syncPostHogConsentFromStorage(): void {
  if (!isPostHogConfigured()) return;

  const consent = getStoredCookieConsent();
  if (consent === "declined") {
    stopSessionRecordingPostHog();
    optOutCapturingPostHog();
    return;
  }
  // accepted OR undecided → allow capture + try start replay
  optInCapturingPostHog();
  startSessionRecordingPostHog();
}

/**
 * Persist opt-out: stops recording AND prevents future captures (across reloads).
 * Use this when the user declines the cookie banner so the decision survives the page reload.
 * `posthog-js` stores the opt-out flag in localStorage; on next init, the SDK reads it and
 * starts in an opted-out state, regardless of `disable_session_recording: false`.
 */
export function optOutCapturingPostHog() {
  if (!isPostHogConfigured()) return;
  try {
    posthog.opt_out_capturing();
  } catch (error) {}
}

/**
 * Reverse of `optOutCapturingPostHog`. Re-enables captures and recording across sessions.
 */
export function optInCapturingPostHog() {
  if (!isPostHogConfigured()) return;
  try {
    posthog.opt_in_capturing();
  } catch (error) {}
}

/**
 * Check if session recording is active
 */
export function isSessionRecordingActivePostHog(): boolean {
  if (!isPostHogConfigured()) return false;

  try {
    return posthog.sessionRecordingStarted();
  } catch (error) {
    return false;
  }
}

/**
 * Get session replay URL (absolute us.posthog.com — safe for Slack).
 */
export function getSessionReplayUrlPostHog(): string | null {
  if (!isPostHogConfigured()) return null;

  try {
    const raw = posthog.get_session_replay_url();
    const sid = posthog.get_session_id?.() || null;
    return normalizePostHogReplayUrlPostHog(raw, sid);
  } catch {
    return null;
  }
}

/**
 * Track feature flag usage
 */
export function trackFeatureFlagPostHog(flagName: string, flagValue: any) {
  if (!isPostHogConfigured()) return;

  posthog.capture("feature_flag_used", {
    flag_name: flagName,
    flag_value: flagValue,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Get feature flag value
 */
export function getFeatureFlagPostHog(flagName: string): any {
  if (!isPostHogConfigured()) return null;

  try {
    return posthog.getFeatureFlag(flagName);
  } catch (error) {
    return null;
  }
}

/**
 * Check if feature is enabled
 */
export function isFeatureEnabledPostHog(featureName: string): boolean {
  if (!isPostHogConfigured()) return false;

  try {
    return posthog.isFeatureEnabled(featureName);
  } catch (error) {
    return false;
  }
}

/**
 * Debug PostHog status
 */
export function debugPostHogStatus() {
  if (typeof window === "undefined") {
    return;
  }
}

/**
 * Track BioTemplatePremium discount offer flow
 */
export function trackDiscountOfferShownPostHog(
  flowName: string,
  planTier: string,
  originalPrice: number,
  discountedPrice: number,
  currency: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("discount_offer_shown", {
    flow_name: flowName,
    plan_tier: planTier,
    original_price: originalPrice,
    discounted_price: discountedPrice,
    discount_percentage: 10,
    currency,
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

export function trackDiscountOfferActionPostHog(
  flowName: string,
  action: "accepted" | "declined",
  planTier: string,
  originalPrice: number,
  discountedPrice: number,
  currency: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("discount_offer_action", {
    flow_name: flowName,
    action,
    plan_tier: planTier,
    original_price: originalPrice,
    discounted_price: discountedPrice,
    discount_percentage: 10,
    currency,
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

export function trackVipVideoOpenedPostHog(
  flowName: string,
  playbackId: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_video_opened", {
    flow_name: flowName,
    playback_id: playbackId.substring(0, 20) + "...",
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

export function trackVipVideoClosedPostHog(
  flowName: string,
  playbackId: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_video_closed", {
    flow_name: flowName,
    playback_id: playbackId.substring(0, 20) + "...",
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

export type DonationFunnelPage = "cumple" | "sorprendeme";

/** Contexto de mercado / gateway para embudos donation_* (cumple intl = USD/Stripe). */
export type DonationTrackContext = {
  currency?: "ARS" | "USD";
  country?: string;
  checkout_gateway?: "mercadopago" | "stripe";
  payment_provider?: "mercadopago" | "stripe";
  language?: "es" | "en";
  is_argentina?: boolean;
  has_email?: boolean;
  has_name?: boolean;
  has_message?: boolean;
  is_custom_amount?: boolean;
  gift_tier?: string;
  donation_id?: string | null;
  payment_reference?: string;
  payment_id?: string;
  error_stage?: string;
  time_on_page_seconds?: number;
  wizard_step?: string;
  checkout_started?: boolean;
  /** PostHog session id for warehouse joins (cumple funnel). */
  cumple_session_id?: string;
  donor_email_domain?: string;
};

function donationContextProps(page: DonationFunnelPage, ctx?: DonationTrackContext) {
  const currency =
    ctx?.currency ?? (page === "cumple" ? undefined : ("ARS" as const));
  return {
    ...(page === "cumple"
      ? { funnel: "cumple" as const, product_type: "birthday_gift" as const, ...getWorkerGeoAnalyticsProps() }
      : {}),
    ...(currency ? { currency } : {}),
    ...(ctx?.country ? { country: ctx.country, payment_country: ctx.country } : {}),
    ...(ctx?.checkout_gateway ? { checkout_gateway: ctx.checkout_gateway } : {}),
    ...(ctx?.payment_provider ? { payment_provider: ctx.payment_provider } : {}),
    ...(ctx?.language ? { market_language: ctx.language } : {}),
    ...(ctx?.is_argentina !== undefined ? { is_argentina: ctx.is_argentina } : {}),
    ...(ctx?.has_email !== undefined ? { has_email: ctx.has_email } : {}),
    ...(ctx?.has_name !== undefined ? { has_name: ctx.has_name } : {}),
    ...(ctx?.has_message !== undefined ? { has_message: ctx.has_message } : {}),
    ...(ctx?.is_custom_amount !== undefined ? { is_custom_amount: ctx.is_custom_amount } : {}),
    ...(ctx?.gift_tier ? { gift_tier: ctx.gift_tier } : {}),
    ...(ctx?.donation_id != null && ctx.donation_id !== ""
      ? { donation_id: ctx.donation_id }
      : {}),
    ...(ctx?.payment_reference
      ? { payment_reference: ctx.payment_reference, preference_id: ctx.payment_reference }
      : {}),
    ...(ctx?.error_stage ? { error_stage: ctx.error_stage } : {}),
    ...(ctx?.time_on_page_seconds !== undefined
      ? { time_on_page_seconds: ctx.time_on_page_seconds }
      : {}),
    ...(ctx?.wizard_step ? { wizard_step: ctx.wizard_step } : {}),
    ...(ctx?.checkout_started !== undefined ? { checkout_started: ctx.checkout_started } : {}),
    ...(ctx?.payment_id ? { payment_id: ctx.payment_id } : {}),
    ...(ctx?.cumple_session_id ? { cumple_session_id: ctx.cumple_session_id } : {}),
    ...(ctx?.donor_email_domain ? { donor_email_domain: ctx.donor_email_domain } : {}),
  };
}

function donationEventProps(page: DonationFunnelPage, replayNearNow = false) {
  return {
    site: BRAND_SOURCE_KEY,
    page,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...getPostHogSlackSessionPropertiesPostHog(
      replayNearNow ? { replayNearNow: true } : undefined,
    ),
    ...getPostHogIdentityPropertiesPostHog(),
    ...(page === "cumple" ? getCumpleAttributionPostHogProps() : {}),
  };
}

/**
 * Track donation page view
 */
export function trackDonationPageViewPostHog(
  page: DonationFunnelPage = "sorprendeme",
  ctx?: DonationTrackContext,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_page_view", {
    ...donationEventProps(page),
    ...donationContextProps(page, ctx),
    funnel_step: "page_view",
  });
}

/**
 * Track donation amount selection
 */
export function trackDonationAmountSelectedPostHog(
  amount: number,
  isCustom: boolean,
  page: DonationFunnelPage = "sorprendeme",
  ctx?: DonationTrackContext,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_amount_selected", {
    ...donationEventProps(page),
    ...donationContextProps(page, ctx),
    amount,
    is_custom_amount: isCustom,
    funnel_step: "amount_selected",
  });
}

/**
 * Track donation details step view (message / email form).
 */
export function trackDonationDetailsViewPostHog(
  amount: number,
  page: DonationFunnelPage = "sorprendeme",
  ctx?: DonationTrackContext,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_details_view", {
    ...donationEventProps(page),
    ...donationContextProps(page, ctx),
    amount,
    funnel_step: "details_view",
  });
}

/**
 * Track embedded MP Payment Brick opened.
 */
export function trackDonationCheckoutOpenedPostHog(
  amount: number,
  preferenceId: string,
  page: DonationFunnelPage = "sorprendeme",
  ctx?: DonationTrackContext,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_checkout_opened", {
    ...donationEventProps(page, true),
    ...donationContextProps(page, ctx),
    amount,
    preference_id: preferenceId,
    checkout_mode: "embedded_brick",
    funnel_step: "checkout_opened",
  });
}

/**
 * Track blocked pay attempt (invalid email, etc.).
 */
export function trackDonationPayBlockedPostHog(
  reason: "invalid_email",
  amount: number,
  page: DonationFunnelPage = "sorprendeme",
  ctx?: DonationTrackContext,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_pay_blocked", {
    ...donationEventProps(page),
    ...donationContextProps(page, ctx),
    block_reason: reason,
    amount,
    funnel_step: "pay_blocked",
  });
}

/**
 * Track donation form submission
 */
export function trackDonationFormSubmittedPostHog(
  amount: number,
  hasName: boolean,
  messageLength: number,
  page: DonationFunnelPage = "sorprendeme",
  ctx?: DonationTrackContext,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_form_submitted", {
    ...donationEventProps(page, true),
    ...donationContextProps(page, ctx),
    amount,
    has_name: hasName,
    message_length: messageLength,
    is_anonymous: !hasName,
    funnel_step: "form_submitted",
  });
}

/**
 * Track donation payment success
 */
export function trackDonationPaymentSuccessPostHog(
  amount: number,
  donationId: string | null,
  page: DonationFunnelPage = "sorprendeme",
  ctx?: DonationTrackContext,
  donorEmail?: string,
) {
  if (!isPostHogConfigured()) return;

  const emailDomain =
    ctx?.donor_email_domain ??
    (donorEmail ? extractEmailDomainPostHog(donorEmail) : undefined);
  const enrichedCtx: DonationTrackContext = {
    ...ctx,
    donation_id: donationId ?? ctx?.donation_id,
    ...(emailDomain ? { donor_email_domain: emailDomain, has_email: true } : {}),
  };
  const base = {
    ...donationEventProps(page, true),
    ...donationContextProps(page, enrichedCtx),
  };
  const currency = ctx?.currency ?? "ARS";
  const paymentProvider = ctx?.payment_provider ?? ctx?.checkout_gateway;

  if (donorEmail?.trim()) {
    identifyDonorByEmailPostHog(donorEmail, {
      funnel: page === "cumple" ? "cumple" : "donation",
      last_donation_amount: amount,
      last_donation_currency: currency,
      ...(donationId ? { last_donation_id: donationId } : {}),
      ...(paymentProvider ? { last_payment_provider: paymentProvider } : {}),
    });
    setUserPropertiesPostHog({
      last_donation_date: new Date().toISOString(),
      last_donation_amount: amount,
      last_donation_currency: currency,
      is_cumple_donor: page === "cumple",
      conversion_stage: "donor",
      ...(emailDomain ? { email_domain: emailDomain } : {}),
    });
  }

  posthog.capture("donation_payment_success", {
    ...base,
    amount,
    donation_id: donationId,
    currency,
    revenue: amount,
    $revenue: amount,
    funnel_step: "payment_completed",
  });

  posthog.capture("conversion", {
    ...base,
    conversion_type: page === "cumple" ? "birthday_gift" : "donation",
    conversion_value: amount,
    currency,
    revenue: amount,
    $revenue: amount,
  });

  if (page === "cumple") {
    posthog.capture("purchase_with_revenue", {
      ...base,
      purchase_id: donationId || `cumple_client_${Date.now()}`,
      revenue: amount,
      $revenue: amount,
      currency: currency.toUpperCase(),
      payment_method: paymentProvider,
      funnel: "cumple",
      product_type: "birthday_gift",
      is_subscription: false,
    });
  }
}

/**
 * Track donation payment error
 */
export function trackDonationPaymentErrorPostHog(
  amount: number,
  errorMessage: string,
  page: DonationFunnelPage = "sorprendeme",
  ctx?: DonationTrackContext,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_payment_error", {
    ...donationEventProps(page, true),
    ...donationContextProps(page, ctx),
    amount,
    error_message: errorMessage,
    funnel_step: "payment_failed",
  });
}

/**
 * Track donation checkout started
 */
export function trackDonationCheckoutStartedPostHog(
  amount: number,
  preferenceId: string,
  page: DonationFunnelPage = "sorprendeme",
  ctx?: DonationTrackContext,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_checkout_started", {
    ...donationEventProps(page, true),
    ...donationContextProps(page, {
      ...ctx,
      payment_reference: preferenceId,
    }),
    amount,
    preference_id: preferenceId,
    checkout_gateway:
      ctx?.checkout_gateway ?? (page === "cumple" ? undefined : "mercadopago"),
    funnel_step: "checkout_started",
  });
}

// ─── Stripe pay failures (cross-surface: Elements, Checkout Session, PaymentIntent API) ─

const STRIPE_PAY_FAILURE_MSG_MAX = 500;

export type StripePayFailureStage =
  | "stripe_js_unavailable"
  | "payment_intent_create"
  | "checkout_session_create"
  | "payment_confirm"
  | "invalid_config"
  | "flow_blocked"
  | "checkout_redirect_failed"
  | "checkout_flow_error";

/**
 * Use one event for dashboards: breakdown by `stage`, `flow_surface`, `stripe_error_code`, `failure_kind`.
 */
export function trackStripePayFailurePostHog(props: {
  stage: StripePayFailureStage;
  /** e.g. checkout_channel_invite_page, mux_video_cta, bio_template_premium_simple */
  flow_surface: string;
  failure_kind?: string;
  error_message?: string;
  http_status?: number;
  stripe_error_code?: string;
  stripe_error_type?: string;
  decline_code?: string;
  amount_cents?: number;
  currency?: string;
  pack_id?: string;
  plan_tier?: string;
  flow_trace_id?: string;
}) {
  if (!isPostHogConfigured()) return;

  const msg = props.error_message
    ? String(props.error_message).slice(0, STRIPE_PAY_FAILURE_MSG_MAX)
    : undefined;

  posthog.capture("stripe_pay_failure", {
    stage: props.stage,
    flow_surface: props.flow_surface,
    failure_kind: props.failure_kind,
    error_message: msg,
    http_status: props.http_status,
    stripe_error_code: props.stripe_error_code || undefined,
    stripe_error_type: props.stripe_error_type || undefined,
    decline_code: props.decline_code || undefined,
    amount_cents: props.amount_cents,
    currency: props.currency,
    pack_id: props.pack_id,
    plan_tier: props.plan_tier,
    flow_trace_id: props.flow_trace_id,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
  });
}

// ─── Media Gallery Tracking (BioLinkTemplate) ─────────────────────────────────

export function trackMediaItemClickPostHog(
  mediaId: string | number,
  mediaType: "video" | "photo",
  mediaTitle?: string,
  positionInGallery?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("media_item_click", {
    media_id: String(mediaId),
    media_type: mediaType,
    media_title: mediaTitle || `Media ${mediaId}`,
    position_in_gallery: positionInGallery,
    template_type: templateType || "BioLinkTemplate",
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackLightboxOpenedPostHog(
  mediaId: string | number,
  source: "gallery_click" | "direct_link",
  mediaType?: "video" | "photo",
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("lightbox_opened", {
    media_id: String(mediaId),
    source,
    media_type: mediaType,
    template_type: templateType || "BioLinkTemplate",
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackLightboxClosedPostHog(
  mediaId: string | number,
  timeSpentSeconds: number,
  viewedItemsCount?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("lightbox_closed", {
    media_id: String(mediaId),
    time_spent_seconds: timeSpentSeconds,
    viewed_items_count: viewedItemsCount,
    template_type: templateType || "BioLinkTemplate",
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackLightboxNavigationPostHog(
  direction: "prev" | "next",
  fromMediaId: string | number,
  toMediaId: string | number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("lightbox_navigation", {
    direction,
    from_media_id: String(fromMediaId),
    to_media_id: String(toMediaId),
    template_type: templateType || "BioLinkTemplate",
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/** Surfaces for `base_template_ui_click` (header, footer, lightbox, etc.) */
export type BaseTemplateClickSurface =
  | "header"
  | "nav_desktop"
  | "nav_mobile"
  | "mobile_menu"
  | "hero"
  | "sobre_mi"
  | "footer"
  | "lightbox";

export type BaseTemplateAnalyticsName =
  | "BaseTemplate"
  | "BaseTemplateBlack"
  | "BaseTemplateInternational";

export function trackBaseTemplateUIClickPostHog(params: {
  templateName: BaseTemplateAnalyticsName;
  elementId: string;
  label: string;
  surface: BaseTemplateClickSurface;
  href?: string;
  countryCode?: string;
  extra?: Record<string, string | number | boolean | undefined>;
}) {
  if (!isPostHogConfigured()) return;

  const geo = getWorkerGeoAnalyticsProps();
  const geoPayload = Object.fromEntries(
    Object.entries(geo).filter(
      ([, v]) => v !== undefined && v !== "",
    ),
  ) as Record<string, string>;

  const slackSession = getPostHogSlackSessionPropertiesPostHog();

  const basePayload = {
    template_type: params.templateName,
    element_id: params.elementId,
    label: params.label,
    surface: params.surface,
    href: params.href,
    country_code:
      params.countryCode ||
      localStorage.getItem("test_country") ||
      undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
    ...geoPayload,
    ...slackSession,
    ...params.extra,
  };

  posthog.capture("base_template_ui_click", basePayload);

  /** Dedicated funnel event for Slack / real-time alerts (matches `base_template_hero_community_cta_click` dashboards). */
  const isHeroCommunityCta =
    params.surface === "hero" && params.elementId === "hero_cta_community";
  if (isHeroCommunityCta) {
    posthog.capture("base_template_hero_community_cta_click", {
      ...basePayload,
      ...getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true }),
      cta_kind: "community",
      cta_destination: params.href,
    });
  }

  const isHeroBirthdayCta =
    params.surface === "hero" &&
    (params.elementId === "hero_cta_birthday" ||
      params.elementId === "hero_cta_birthday_broadcast");
  if (isHeroBirthdayCta) {
    posthog.capture("base_template_hero_birthday_cta_click", {
      ...basePayload,
      ...getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true }),
      cta_kind: "birthday",
      cta_destination: params.href,
      funnel: "cumple",
      product_type: "birthday_gift",
    });
  }

  const isHeroMejoresAmigosCta =
    params.surface === "hero" && params.elementId === "hero_cta_mejores_amigos";
  if (isHeroMejoresAmigosCta) {
    posthog.capture("base_template_hero_mejores_amigos_cta_click", {
      ...basePayload,
      ...getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true }),
      cta_kind: "mejores_amigos",
      cta_destination: params.href,
      funnel: "mejores_amigos",
      product_type: "instagram_close_friends",
      ...params.extra,
    });
  }
}

/**
 * Dedicated funnel event: impresión del CTA Mejores Amigos (hero visible).
 */
export function trackMejoresAmigosCtaImpressionPostHog(params: {
  href: string;
  label?: string;
  templateName?: string;
  countryCode?: string;
  ctaTier?: string;
  mpPlanId?: string;
  ctaResolved?: string;
  ctaGeoSource?: string;
  showBestFriends?: boolean;
  showBirthdayLegacy?: boolean;
  extra?: Record<string, string | number | boolean | undefined>;
}) {
  if (!isPostHogConfigured()) return;

  const geo = getWorkerGeoAnalyticsProps();
  const geoPayload = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;

  posthog.capture("mejores_amigos_cta_impression", {
    site: BRAND_SOURCE_KEY,
    funnel: "mejores_amigos",
    product_type: "instagram_close_friends",
    cta_destination: params.href,
    cta_label: params.label,
    template_type: params.templateName,
    cta_tier: params.ctaTier,
    mp_plan_id: params.mpPlanId,
    cta_resolved: params.ctaResolved,
    cta_geo_source: params.ctaGeoSource,
    show_best_friends: params.showBestFriends ?? false,
    show_birthday_legacy: params.showBirthdayLegacy ?? false,
    country_code:
      params.countryCode ||
      (geoPayload.country as string | undefined) ||
      localStorage.getItem("test_country") ||
      undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    funnel_step: "cta_impression",
    ...geoPayload,
    ...params.extra,
  });
}

/**
 * Dedicated funnel event: click en CTA Mejores Amigos (cualquier superficie).
 */
export function trackMejoresAmigosCtaClickPostHog(params: {
  source: string;
  href: string;
  label?: string;
  templateName?: string;
  elementId?: string;
  surface?: string;
  countryCode?: string;
  ctaTier?: string;
  mpPlanId?: string;
  ctaResolved?: string;
  ctaGeoSource?: string;
  showBestFriends?: boolean;
  showBirthdayLegacy?: boolean;
  extra?: Record<string, string | number | boolean | undefined>;
}) {
  if (!isPostHogConfigured()) return;

  const geo = getWorkerGeoAnalyticsProps();
  const geoPayload = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;

  const slackSession = getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true });

  posthog.capture("mejores_amigos_cta_click", {
    site: BRAND_SOURCE_KEY,
    funnel: "mejores_amigos",
    product_type: "instagram_close_friends",
    cta_source: params.source,
    cta_destination: params.href,
    cta_label: params.label,
    template_type: params.templateName,
    element_id: params.elementId,
    surface: params.surface,
    cta_tier: params.ctaTier,
    mp_plan_id: params.mpPlanId,
    cta_resolved: params.ctaResolved,
    cta_geo_source: params.ctaGeoSource,
    show_best_friends: params.showBestFriends ?? false,
    show_birthday_legacy: params.showBirthdayLegacy ?? false,
    country_code:
      params.countryCode ||
      (geoPayload.country as string | undefined) ||
      localStorage.getItem("test_country") ||
      undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    funnel_step: "cta_click",
    ...geoPayload,
    ...slackSession,
    ...params.extra,
  });
}

/**
 * Dedicated funnel event: click en CTA de regalo cumpleaños (cualquier superficie).
 */
export function trackBirthdayCtaClickPostHog(params: {
  source: CumpleCtaSource;
  href: string;
  label?: string;
  templateName?: string;
  elementId?: string;
  surface?: string;
  countryCode?: string;
  workerVariant?: string;
  extra?: Record<string, string | number | boolean | undefined>;
}) {
  if (!isPostHogConfigured()) return;

  const geo = getWorkerGeoAnalyticsProps();
  const geoPayload = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;

  const slackSession = getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true });

  posthog.capture("birthday_cta_click", {
    site: BRAND_SOURCE_KEY,
    funnel: "cumple",
    product_type: "birthday_gift",
    cta_source: params.source,
    cta_destination: params.href,
    cta_label: params.label,
    template_type: params.templateName,
    element_id: params.elementId,
    surface: params.surface,
    worker_variant: params.workerVariant,
    country_code:
      params.countryCode ||
      (geoPayload.country as string | undefined) ||
      localStorage.getItem("test_country") ||
      undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    funnel_step: "cta_click",
    ...geoPayload,
    ...slackSession,
    ...params.extra,
  });
}

// ─── Video Analytics ─────────────────────────────────────────────────────────

export function trackVideoPlayPostHog(
  videoId: string | number,
  videoTitle?: string,
  playbackId?: string,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_play", {
    video_id: String(videoId),
    video_title: videoTitle || `Video ${videoId}`,
    playback_id: playbackId ? playbackId.substring(0, 20) + "..." : undefined,
    template_type: templateType,
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackVideoPausePostHog(
  videoId: string | number,
  currentTimeSeconds?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_pause", {
    video_id: String(videoId),
    current_time_seconds: currentTimeSeconds,
    template_type: templateType,
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackVideoCompletePostHog(
  videoId: string | number,
  durationSeconds?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_complete", {
    video_id: String(videoId),
    duration_seconds: durationSeconds,
    template_type: templateType,
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackVideoProgressPostHog(
  videoId: string | number,
  progressPercent: number,
  currentTimeSeconds?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_progress", {
    video_id: String(videoId),
    progress_percent: progressPercent,
    current_time_seconds: currentTimeSeconds,
    template_type: templateType,
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

// ─── BioTemplatePremium Checkout Flow ────────────────────────────────────────

export function trackPlanButtonClickPostHog(
  planTier: "standard" | "vip" | "free",
  price: number,
  currency: string,
  flowName: string,
  country?: string,
  hasVideo?: boolean,
  /** Default keeps dashboards that filter on BioTemplatePremium unchanged. */
  templateType: string = "BioTemplatePremium",
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("plan_button_click", {
    plan_tier: planTier,
    price,
    currency,
    flow_name: flowName,
    country_code: country,
    has_video: hasVideo,
    template_type: templateType,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackCheckoutPanelOpenedPostHog(
  planTier: "standard" | "vip",
  source: "direct_button" | "video_cta" | "discount_accepted",
  price: number,
  currency: string,
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("checkout_panel_opened", {
    plan_tier: planTier,
    source,
    price,
    currency,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackCheckoutPanelClosedPostHog(
  planTier: "standard" | "vip",
  emailProvided: boolean,
  timeInCheckoutSeconds: number,
  reason: "user_closed" | "completed" | "error",
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("checkout_panel_closed", {
    plan_tier: planTier,
    email_provided: emailProvided,
    time_in_checkout_seconds: timeInCheckoutSeconds,
    reason,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackEmailInputInteractionPostHog(
  action: "focus" | "blur",
  planTier?: "standard" | "vip",
  hasValue?: boolean,
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("email_input_interaction", {
    action,
    plan_tier: planTier,
    has_value: hasValue,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackEmailValidationErrorPostHog(
  errorType: "empty" | "invalid",
  planTier?: "standard" | "vip",
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("email_validation_error", {
    error_type: errorType,
    plan_tier: planTier,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackCheckoutAbandonmentPostHog(
  planTier: "standard" | "vip",
  emailProvided: boolean,
  timeInCheckoutSeconds: number,
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("checkout_abandonment", {
    plan_tier: planTier,
    email_provided: emailProvided,
    time_in_checkout_seconds: timeInCheckoutSeconds,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

// ─── Purchase Revenue Tracking ───────────────────────────────────────────────

export function trackPurchaseWithRevenuePostHog(
  purchaseId: string,
  revenue: number,
  currency: string,
  planTier?: string,
  planName?: string,
  paymentMethod?: string,
  isSubscription?: boolean,
  subscriptionId?: string,
  userId?: string,
  country?: string,
  stripeSessionId?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("purchase_with_revenue", {
    purchase_id: purchaseId,
    revenue,
    currency: currency.toUpperCase(),
    plan_tier: planTier,
    plan_name: planName,
    payment_method: paymentMethod,
    is_subscription: isSubscription,
    subscription_id: subscriptionId,
    user_id: userId,
    country_code: country,
    stripe_session_id: stripeSessionId,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });

  trackConversionPostHog("purchase", "premium_subscription", revenue, currency);
}

export function trackSubscriptionStartedPostHog(
  subscriptionId: string,
  planTier: string,
  price: number,
  currency: string,
  country?: string,
  userId?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("subscription_started", {
    subscription_id: subscriptionId,
    plan_tier: planTier,
    price,
    currency: currency.toUpperCase(),
    country_code: country,
    user_id: userId,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

// ─── User Identification ─────────────────────────────────────────────────────

/** Email domain for breakdowns without storing full address on events. */
export function extractEmailDomainPostHog(email: string): string | null {
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf("@");
  if (at < 1 || at >= trimmed.length - 1) return null;
  return trimmed.slice(at + 1);
}

/**
 * Identify a donor/regalo fan by email — links anonymous funnel events to the person.
 */
export function identifyDonorByEmailPostHog(
  email: string,
  properties?: Record<string, unknown>,
) {
  const normalized = email.trim().toLowerCase();
  if (!normalized.includes("@")) return;

  identifyUserByEmailPostHog(normalized, {
    email_domain: extractEmailDomainPostHog(normalized),
    donor_identified_at: new Date().toISOString(),
    ...properties,
  });
}

/**
 * Identify user by email (called at checkout when email is provided).
 * PostHog SDK auto-captures browser, language, timezone, screen_resolution.
 * Calling identify() links all prior anonymous events to this person profile.
 */
export function identifyUserByEmailPostHog(
  email: string,
  properties?: Record<string, unknown>
) {
  if (!isPostHogConfigured()) return;

  posthog.identify(email, {
    email,
    ...properties,
    user_region: localStorage.getItem("user_region") || "LATAM",
    test_country: localStorage.getItem("test_country") || null,
  });
}

// ─── MediaKit Tracking (/mediakit, /mediakit/:slug) ──────────────────────────
// Always attach kit identity so multi-kit verticals (fashion, beauty…) are filterable.
// Super-properties already include `site` = BRAND_SOURCE_KEY.

export type MediaKitVariant = "intro" | "desktop";
export type MediaKitNavMethod =
  | "button"
  | "keyboard"
  | "swipe"
  | "thumbnail"
  | "scroll"
  | "hash";

export type MediaKitCtaType =
  | "email_brief"
  | "instagram"
  | "external_link"
  | "other";

export type MediaKitContentSource = "cms" | "fallback";

/** Shared kit identity for every mediakit_* event (multi-kit + layout). */
export type MediaKitTrackContext = {
  kit_slug: string;
  kit_id?: string | null;
  /** Admin display title (e.g. Fashion) — unique per vertical kit */
  kit_title?: string | null;
  is_general?: boolean;
  content_version?: number;
  content_source?: MediaKitContentSource;
  /** Comma-separated enabled section types in order */
  enabled_sections?: string;
  total_sections?: number;
  public_path?: string;
  variant?: MediaKitVariant;
  /** Creator name from this kit's profile (varies if CMS edits it) */
  creator_name?: string | null;
  brand_name?: string | null;
  /** How many Cloudinary images are in this kit payload */
  image_count?: number | null;
};

function mediaKitDeviceProps() {
  return {
    is_mobile:
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      ),
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    referrer: document.referrer || null,
    page_path: window.location.pathname,
    page_search: window.location.search || null,
    page_hash: window.location.hash || null,
    timestamp: new Date().toISOString(),
  };
}

function mediaKitKitProps(ctx?: MediaKitTrackContext | null) {
  if (!ctx) return { product: "mediakit" as const };
  return {
    product: "mediakit" as const,
    kit_slug: ctx.kit_slug || "general",
    kit_id: ctx.kit_id ?? null,
    kit_title: ctx.kit_title ?? null,
    is_general: ctx.is_general ?? null,
    content_version: ctx.content_version ?? null,
    content_source: ctx.content_source ?? null,
    enabled_sections: ctx.enabled_sections ?? null,
    total_sections: ctx.total_sections ?? null,
    public_path: ctx.public_path ?? window.location.pathname,
    variant: ctx.variant ?? "desktop",
    creator_name: ctx.creator_name ?? null,
    brand_name: ctx.brand_name ?? null,
    image_count: ctx.image_count ?? null,
  };
}

/**
 * Register PostHog group for this media kit so every event can be broken down
 * by vertical (fashion vs general). Safe no-op if groups unsupported.
 */
export function identifyMediaKitGroupPostHog(ctx: MediaKitTrackContext) {
  if (!isPostHogConfigured()) return;
  const key = ctx.kit_slug || "general";
  try {
    // posthog-js group API — enables "media_kit" breakdown in insights
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ph = posthog as any;
    if (typeof ph.group === "function") {
      ph.group("media_kit", key, {
        name: ctx.kit_title || key,
        kit_slug: key,
        kit_id: ctx.kit_id ?? null,
        is_general: Boolean(ctx.is_general),
        public_path: ctx.public_path ?? null,
        creator_name: ctx.creator_name ?? null,
        brand_name: ctx.brand_name ?? null,
        content_version: ctx.content_version ?? null,
      });
    }
    posthog.register({
      mediakit_slug: key,
      mediakit_title: ctx.kit_title || key,
      mediakit_is_general: Boolean(ctx.is_general),
    });
  } catch {
    /* ignore */
  }
}

/** Fired once when the MediaKit viewer is ready (content resolved). */
export function trackMediaKitViewPostHog(
  variant: MediaKitVariant,
  totalPages: number,
  isFullscreenMode: boolean,
  ctx?: MediaKitTrackContext | null,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_view", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    variant: ctx?.variant ?? variant,
    total_pages: totalPages,
    total_sections: ctx?.total_sections ?? totalPages,
    is_fullscreen_mode: isFullscreenMode,
  });
}

/** CMS load outcome — know when brands see seed defaults vs published JSONB. */
export function trackMediaKitContentLoadedPostHog(
  ctx: MediaKitTrackContext & {
    load_error?: string | null;
    loading_ms?: number;
  },
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_content_loaded", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    content_source: ctx.content_source,
    load_error: ctx.load_error ?? null,
    loading_ms: ctx.loading_ms ?? null,
    used_fallback: ctx.content_source === "fallback",
  });
}

/** @deprecated password gate removed — kept for historical event schema */
export function trackMediaKitGateShownPostHog() {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_gate_shown", {
    product: "mediakit",
    ...mediaKitDeviceProps(),
  });
}

/** @deprecated password gate removed — kept for historical event schema */
export function trackMediaKitPasswordAttemptPostHog(success: boolean) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_password_attempt", {
    product: "mediakit",
    success,
    ...mediaKitDeviceProps(),
  });
}

/** Fired every time the visible section changes (scroll spy or nav). */
export function trackMediaKitPageViewPostHog(
  pageNumber: number,
  pageTitle: string,
  totalPages: number,
  navigationMethod: MediaKitNavMethod,
  isFullscreen: boolean,
  variant: MediaKitVariant,
  ctx?: (MediaKitTrackContext & {
    section_type?: string;
    section_id?: string;
  }) | null,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_page_view", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    page_number: pageNumber,
    page_title: pageTitle,
    section_index: pageNumber,
    section_label: pageTitle,
    section_type: ctx?.section_type ?? null,
    section_id: ctx?.section_id ?? null,
    total_pages: totalPages,
    total_sections: ctx?.total_sections ?? totalPages,
    navigation_method: navigationMethod,
    is_fullscreen: isFullscreen,
    is_first_page: pageNumber === 1,
    is_last_page: pageNumber === totalPages,
    variant: ctx?.variant ?? variant,
  });
}

/** Fired when user intentionally navigates (nav pill / hash). */
export function trackMediaKitNavigationPostHog(
  direction: "next" | "prev" | "jump",
  method: MediaKitNavMethod,
  fromPage: number,
  toPage: number,
  totalPages: number,
  isFullscreen: boolean,
  variant: MediaKitVariant,
  ctx?: (MediaKitTrackContext & {
    from_section_id?: string;
    to_section_id?: string;
    from_section_type?: string;
    to_section_type?: string;
  }) | null,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_navigation", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    direction,
    navigation_method: method,
    from_page: fromPage,
    to_page: toPage,
    from_section_id: ctx?.from_section_id ?? null,
    to_section_id: ctx?.to_section_id ?? null,
    from_section_type: ctx?.from_section_type ?? null,
    to_section_type: ctx?.to_section_type ?? null,
    total_pages: totalPages,
    total_sections: ctx?.total_sections ?? totalPages,
    is_fullscreen: isFullscreen,
    variant: ctx?.variant ?? variant,
  });
}

/** Time spent on a section when leaving it (scroll spy or unmount). */
export function trackMediaKitSectionDwellPostHog(
  ctx: MediaKitTrackContext & {
    section_id: string;
    section_type: string;
    section_label: string;
    section_index: number;
    dwell_ms: number;
  },
) {
  if (!isPostHogConfigured()) return;
  if (ctx.dwell_ms < 250) return; // ignore flicker

  posthog.capture("mediakit_section_dwell", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    section_id: ctx.section_id,
    section_type: ctx.section_type,
    section_label: ctx.section_label,
    section_index: ctx.section_index,
    dwell_ms: Math.round(ctx.dwell_ms),
    dwell_seconds: Math.round(ctx.dwell_ms / 1000),
  });
}

/** CTA / outbound commercial actions (brief, Instagram, mailto). */
export function trackMediaKitCtaClickPostHog(
  ctx: MediaKitTrackContext & {
    cta_type: MediaKitCtaType;
    cta_label?: string;
    href?: string;
    section_id?: string;
    section_type?: string;
  },
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_cta_click", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    cta_type: ctx.cta_type,
    cta_label: ctx.cta_label ?? null,
    href: ctx.href ?? null,
    section_id: ctx.section_id ?? null,
    section_type: ctx.section_type ?? null,
  });

  posthog.capture("conversion_funnel_step", {
    funnel_step: `mediakit_cta_${ctx.cta_type}`,
    product: "mediakit",
    kit_slug: ctx.kit_slug,
    is_general: ctx.is_general ?? null,
    page_path: window.location.pathname,
  });
}

/** Fired when entering or exiting fullscreen (legacy SPA) */
export function trackMediaKitFullscreenTogglePostHog(
  action: "open" | "close",
  currentPage: number,
  variant: MediaKitVariant,
  ctx?: MediaKitTrackContext | null,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_fullscreen", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    action,
    current_page: currentPage,
    variant: ctx?.variant ?? variant,
  });
}

/** Fired when the PDF download link is clicked */
export function trackMediaKitDownloadClickedPostHog(
  variant: MediaKitVariant,
  ctx?: MediaKitTrackContext | null,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_download_clicked", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    variant: ctx?.variant ?? variant,
  });

  posthog.capture("conversion_funnel_step", {
    funnel_step: "mediakit_download",
    variant: ctx?.variant ?? variant,
    kit_slug: ctx?.kit_slug ?? null,
    page_path: window.location.pathname,
  });
}

/** Fired once when all enabled sections have been seen */
export function trackMediaKitCompletedPostHog(
  totalPages: number,
  timeSpentSeconds: number,
  variant: MediaKitVariant,
  ctx?: (MediaKitTrackContext & {
    sections_viewed?: string;
  }) | null,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_completed", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    total_pages: totalPages,
    total_sections: ctx?.total_sections ?? totalPages,
    time_spent_seconds: timeSpentSeconds,
    sections_viewed: ctx?.sections_viewed ?? null,
    variant: ctx?.variant ?? variant,
  });

  posthog.capture("conversion_funnel_step", {
    funnel_step: "mediakit_completed",
    kit_slug: ctx?.kit_slug ?? null,
    page_path: window.location.pathname,
  });
}

/** Fired when the user leaves the mediakit (unmount), with total session time */
export function trackMediaKitSessionEndPostHog(
  totalPages: number,
  pagesViewed: number,
  timeSpentSeconds: number,
  maxPageReached: number,
  variant: MediaKitVariant,
  ctx?: (MediaKitTrackContext & {
    sections_viewed?: string;
    last_section_id?: string;
    cta_clicks?: number;
    images_impressed?: number;
    images_loaded?: number;
    images_failed?: number;
    max_scroll_pct?: number;
    most_attended_section?: string | null;
    most_attended_ratio?: number | null;
    image_public_ids_seen?: string | null;
  }) | null,
) {
  if (!isPostHogConfigured()) return;

  const total = Math.max(1, totalPages);

  posthog.capture("mediakit_session_end", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    total_pages: totalPages,
    total_sections: ctx?.total_sections ?? totalPages,
    pages_viewed: pagesViewed,
    pages_viewed_pct: Math.round((pagesViewed / total) * 100),
    max_page_reached: maxPageReached,
    time_spent_seconds: timeSpentSeconds,
    completed: pagesViewed >= total,
    sections_viewed: ctx?.sections_viewed ?? null,
    last_section_id: ctx?.last_section_id ?? null,
    cta_clicks: ctx?.cta_clicks ?? 0,
    images_impressed: ctx?.images_impressed ?? 0,
    images_loaded: ctx?.images_loaded ?? 0,
    images_failed: ctx?.images_failed ?? 0,
    max_scroll_pct: ctx?.max_scroll_pct ?? 0,
    most_attended_section: ctx?.most_attended_section ?? null,
    most_attended_ratio: ctx?.most_attended_ratio ?? null,
    image_public_ids_seen: ctx?.image_public_ids_seen ?? null,
    variant: ctx?.variant ?? variant,
  });
}

/** Max intersection ratio while a section is on screen (attention proxy). */
export function trackMediaKitSectionAttentionPostHog(
  ctx: MediaKitTrackContext & {
    section_id: string;
    section_type: string;
    section_label: string;
    section_index: number;
    max_intersection_ratio: number;
    visible_ms: number;
  },
) {
  if (!isPostHogConfigured()) return;
  if (ctx.visible_ms < 400 && ctx.max_intersection_ratio < 0.35) return;

  posthog.capture("mediakit_section_attention", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    section_id: ctx.section_id,
    section_type: ctx.section_type,
    section_label: ctx.section_label,
    section_index: ctx.section_index,
    max_intersection_ratio: Math.round(ctx.max_intersection_ratio * 1000) / 1000,
    visible_ms: Math.round(ctx.visible_ms),
    visible_seconds: Math.round(ctx.visible_ms / 1000),
    attention_score: Math.round(
      ctx.max_intersection_ratio * Math.min(ctx.visible_ms / 1000, 60) * 10,
    ) / 10,
  });
}

/** Image entered viewport (what they look at). */
export function trackMediaKitImageImpressionPostHog(
  ctx: MediaKitTrackContext & {
    public_id: string;
    image_variant?: string;
    image_role?: string;
    section_id?: string;
    section_type?: string;
    alt?: string;
    intersection_ratio?: number;
  },
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_image_impression", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    public_id: ctx.public_id,
    cloudinary_public_id: ctx.public_id,
    image_variant: ctx.image_variant ?? null,
    image_role: ctx.image_role ?? null,
    section_id: ctx.section_id ?? null,
    section_type: ctx.section_type ?? null,
    alt: ctx.alt ?? null,
    intersection_ratio:
      typeof ctx.intersection_ratio === "number"
        ? Math.round(ctx.intersection_ratio * 1000) / 1000
        : null,
  });
}

/** Cloudinary delivery load metrics (client-side: timing + dimensions). */
export function trackMediaKitImageLoadPostHog(
  ctx: MediaKitTrackContext & {
    public_id: string;
    image_variant?: string;
    image_role?: string;
    section_id?: string;
    section_type?: string;
    success: boolean;
    load_ms?: number | null;
    natural_width?: number | null;
    natural_height?: number | null;
    transfer_size?: number | null;
    encoded_body_size?: number | null;
    src_host?: string | null;
    error_message?: string | null;
  },
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_image_load", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    public_id: ctx.public_id,
    cloudinary_public_id: ctx.public_id,
    image_variant: ctx.image_variant ?? null,
    image_role: ctx.image_role ?? null,
    section_id: ctx.section_id ?? null,
    section_type: ctx.section_type ?? null,
    success: ctx.success,
    load_ms: ctx.load_ms ?? null,
    natural_width: ctx.natural_width ?? null,
    natural_height: ctx.natural_height ?? null,
    transfer_size: ctx.transfer_size ?? null,
    encoded_body_size: ctx.encoded_body_size ?? null,
    src_host: ctx.src_host ?? null,
    error_message: ctx.error_message ?? null,
    is_cloudinary:
      Boolean(ctx.src_host?.includes("cloudinary")) ||
      Boolean(ctx.public_id && !ctx.public_id.startsWith("http")),
  });
}

/** Time an image stayed meaningfully visible. */
export function trackMediaKitImageDwellPostHog(
  ctx: MediaKitTrackContext & {
    public_id: string;
    image_variant?: string;
    image_role?: string;
    section_id?: string;
    section_type?: string;
    dwell_ms: number;
    max_intersection_ratio: number;
  },
) {
  if (!isPostHogConfigured()) return;
  if (ctx.dwell_ms < 300) return;

  posthog.capture("mediakit_image_dwell", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    public_id: ctx.public_id,
    cloudinary_public_id: ctx.public_id,
    image_variant: ctx.image_variant ?? null,
    image_role: ctx.image_role ?? null,
    section_id: ctx.section_id ?? null,
    section_type: ctx.section_type ?? null,
    dwell_ms: Math.round(ctx.dwell_ms),
    dwell_seconds: Math.round(ctx.dwell_ms / 1000),
    max_intersection_ratio:
      Math.round(ctx.max_intersection_ratio * 1000) / 1000,
  });
}

/** Scroll depth milestones within the media kit scroller. */
export function trackMediaKitScrollDepthPostHog(
  ctx: MediaKitTrackContext & {
    scroll_pct: number;
    active_section_id?: string;
  },
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_scroll_depth", {
    ...mediaKitKitProps(ctx),
    ...mediaKitDeviceProps(),
    scroll_pct: ctx.scroll_pct,
    active_section_id: ctx.active_section_id ?? null,
  });
}

// ─── MediaKit Admin CMS ─────────────────────────────────────────────────────

export type MediaKitAdminAction =
  | "list_view"
  | "editor_view"
  | "save"
  | "create"
  | "delete"
  | "set_general"
  | "restore_defaults";

export function trackMediaKitAdminActionPostHog(params: {
  action: MediaKitAdminAction;
  kit_slug?: string | null;
  kit_id?: string | null;
  is_general?: boolean | null;
  status?: string | null;
  title?: string | null;
  success?: boolean;
  error?: string | null;
  /** JSON-ish summary of layout (enabled types in order) */
  layout_enabled?: string | null;
  content_version?: number | null;
}) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_admin_action", {
    product: "mediakit",
    surface: "admin",
    action: params.action,
    kit_slug: params.kit_slug ?? null,
    kit_id: params.kit_id ?? null,
    is_general: params.is_general ?? null,
    status: params.status ?? null,
    title: params.title ?? null,
    success: params.success ?? true,
    error: params.error ?? null,
    layout_enabled: params.layout_enabled ?? null,
    content_version: params.content_version ?? null,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

// ─── VIP Custom Payment Funnel (/vip/:nombre) ────────────────────────────────

export function trackVipPageViewPostHog(
  nombre: string,
  country: string,
  isAvailable: boolean,
  paymentMethod: "mercadopago" | "stripe"
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_page_view", {
    vip_nombre: nombre,
    country_code: country,
    is_available: isAvailable,
    payment_method: paymentMethod,
    page_path: window.location.pathname,
    page_url: window.location.href,
    referrer: document.referrer,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
    is_mobile:
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ),
  });
}

export function trackVipUnavailablePostHog(
  nombre: string,
  country: string,
  paymentMethod: "mercadopago" | "stripe"
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_unavailable_shown", {
    vip_nombre: nombre,
    country_code: country,
    payment_method: paymentMethod,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackVipStepChangePostHog(
  nombre: string,
  from: string,
  to: string,
  country: string,
  currency: "ARS" | "USD",
  amount?: number
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_step_change", {
    vip_nombre: nombre,
    from_step: from,
    to_step: to,
    country_code: country,
    currency,
    amount,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackVipAmountSelectedPostHog(
  nombre: string,
  amount: number,
  currency: "ARS" | "USD",
  country: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_amount_selected", {
    vip_nombre: nombre,
    amount,
    currency,
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackVipCheckoutStartedPostHog(
  nombre: string,
  amount: number,
  currency: "ARS" | "USD",
  country: string,
  paymentMethod: "mercadopago" | "stripe",
  hasEmail: boolean,
  hasName: boolean,
  hasMessage: boolean
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_checkout_started", {
    vip_nombre: nombre,
    amount,
    currency,
    country_code: country,
    payment_method: paymentMethod,
    has_email: hasEmail,
    has_name: hasName,
    has_message: hasMessage,
    funnel_step: "checkout_initiated",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });

  posthog.capture("conversion_funnel_step", {
    funnel_step: "vip_checkout_started",
    vip_nombre: nombre,
    amount,
    currency,
    country_code: country,
    payment_method: paymentMethod,
  });
}

export function trackVipPaymentResultPostHog(
  nombre: string,
  result: "success" | "error" | "pending",
  amount: number,
  currency: "ARS" | "USD",
  country: string,
  paymentMethod: "mercadopago" | "stripe",
  donationId?: string | null,
  errorMessage?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_payment_result", {
    vip_nombre: nombre,
    result,
    amount,
    currency,
    country_code: country,
    payment_method: paymentMethod,
    donation_id: donationId,
    error_message: result === "error" ? errorMessage : undefined,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });

  if (result === "success") {
    posthog.capture("conversion", {
      conversion_type: "vip_payment",
      value: amount,
      currency,
      vip_nombre: nombre,
      country_code: country,
      payment_method: paymentMethod,
      donation_id: donationId,
    });
  }
}

/**
 * Tras completar perfil post-compra (session_id en query). Correlación soporte/disputas:
 * persona PostHog ↔ sesión de Checkout en Stripe ↔ metadata de membresía.
 */
export function capturePostPurchaseProfileCompletedPostHog(payload: {
  plan_tier: string | null;
  from_vip: boolean;
  from_basic: boolean;
  from_standard?: boolean;
  stripe_session_id: string | null;
  mercadopago_subscription_id?: string | null;
}) {
  if (!isPostHogConfigured()) return;

  posthog.capture("post_purchase_profile_completed", {
    plan_tier: payload.plan_tier || undefined,
    from_vip: payload.from_vip,
    from_basic: payload.from_basic,
    from_standard: payload.from_standard ?? false,
    has_stripe_session: Boolean(payload.stripe_session_id),
    stripe_session_id: payload.stripe_session_id || undefined,
    has_mercadopago_subscription: Boolean(payload.mercadopago_subscription_id),
    mercadopago_subscription_id: payload.mercadopago_subscription_id || undefined,
    page_path: window.location.pathname,
  });
}

/**
 * SEO runtime: confirma qué og:image quedó en el DOM tras useSEO.
 * Permite detectar regresiones (imagedelivery 403, pack OG inexistente, etc.).
 */
export function captureSeoMetaAppliedPostHog(payload: {
  og_image_url: string;
  page_path: string;
  uses_imagedelivery: boolean;
  uses_canonical_og: boolean;
}) {
  if (!isPostHogConfigured()) return;

  posthog.capture("seo_meta_applied", {
    og_image_url: payload.og_image_url,
    page_path: payload.page_path,
    uses_imagedelivery: payload.uses_imagedelivery,
    uses_canonical_og: payload.uses_canonical_og,
    brand_og_expected: payload.uses_canonical_og ? undefined : "site/og.png",
  });

  if (payload.uses_imagedelivery || !payload.uses_canonical_og) {
    posthog.capture("brand_asset_health_failure", {
      check_source: "client_seo",
      passed: false,
      failed_assets: payload.uses_imagedelivery
        ? "og:image uses imagedelivery.net"
        : `og:image not canonical: ${payload.og_image_url}`,
      og_image_url: payload.og_image_url,
      page_path: payload.page_path,
    });
  }
}

// Export PostHog instance for direct access
export { posthog };
